import { useEffect, useState, Fragment, useCallback, useRef } from "react";
import { MuiMaterials } from '../@index.js';
import { GlobalTheme } from '../themes/GlobalTheme';
import { useNavigate } from "react-router-dom";
import * as global from '../data/global';
import { saveSelectedSite, markSite3dPinned } from '../data/global';
import HoldTooltip from './HoldTooltip.js';
import CarbonDioxideChartTile from './CarbonDioxideChartTile.js';
import { createApiEndpoint } from '../api/createApiEndpoint.js';
import CcrPartList from './CcrPartList.js';
import { useAuth } from '../provider/authProvider.js';
import Spinner from './windowChrome/Spinner.js';
import { outlinedActionButtonSx } from './windowChrome/actionButtons.js';
import { confirmDialogActionsSx, confirmDialogButtonSx } from './windowChrome/ConfirmDialog.js';
import { openSiteWindow, SITE_WINDOW_IDS, getSiteCardMenuItems } from './windowChrome/siteWindowNav.js';
import { getLastScanAlertReasons, lastScanAlertTooltip } from './lastScanAlert.js';
import { withScrollHintMenuProps } from './windowChrome/ScrollHintPaper.js';
import StraightIcon from '@mui/icons-material/Straight';
import TuneIcon from '@mui/icons-material/Tune';
import MenuIcon from '@mui/icons-material/Menu';


// Helper function to get theme-aware colors
const getThemeColor = (lightColor, darkColor) => {
    if (global.gw.theme === 'light') {
        return lightColor;
    }
    return darkColor;
};

const CARD_BIN_GRAPHIC_SIZE = 80;
const CARD_BIN_SHAPE_INSET = 8;
const CARD_BIN_SHAPE_SIZE = 64;
const CARD_BIN_FLAT_HOUSE_WIDTH = CARD_BIN_SHAPE_SIZE * 1.5;
const CARD_BIN_FLAT_HOUSE_HEIGHT = CARD_BIN_SHAPE_SIZE;
const CARD_BIN_FLAT_HOUSE_GRAPHIC_WIDTH =
    CARD_BIN_SHAPE_INSET * 2 + CARD_BIN_FLAT_HOUSE_WIDTH;
const CARD_BIN_RADIUS = 32;
const CARD_BIN_CENTER = 40;
const CARD_BIN_GRADE_STROKE = 8.5;

const cardBinRectGradePath = (shapeX, shapeY, shapeWidth, shapeHeight) => {
    const h = CARD_BIN_GRADE_STROKE / 2;
    const x0 = shapeX + h;
    const y0 = shapeY + h;
    const x1 = shapeX + shapeWidth - h;
    const y1 = shapeY + shapeHeight - h;
    const midX = (x0 + x1) / 2;
    return `M ${midX} ${y1} L ${x0} ${y1} L ${x0} ${y0} L ${x1} ${y0} L ${x1} ${y1} L ${midX} ${y1}`;
};

const cardBinRectGradePerimeter = (shapeWidth, shapeHeight) =>
    2 * (shapeWidth + shapeHeight - CARD_BIN_GRADE_STROKE);

const cardBinFlatHouseShape = () => ({
    x: CARD_BIN_SHAPE_INSET,
    y: CARD_BIN_SHAPE_INSET,
    width: CARD_BIN_FLAT_HOUSE_WIDTH,
    height: CARD_BIN_FLAT_HOUSE_HEIGHT,
});

const CardBinLevelClipDef = ({ clipId, fillRatio, shapeX, shapeY, shapeWidth, shapeHeight }) => {
    if (fillRatio == null || fillRatio <= 0) return null;
    const fillHeight = shapeHeight * Math.min(1, fillRatio);
    return (
        <defs>
            <clipPath id={clipId}>
                <rect
                    x={shapeX}
                    y={shapeY + shapeHeight - fillHeight}
                    width={shapeWidth}
                    height={fillHeight}
                />
            </clipPath>
        </defs>
    );
};

const CardBinShapeBackground = ({ isFlatHouse, fillRatio, clipId }) => {
    const flatShape = isFlatHouse ? cardBinFlatHouseShape() : null;
    const clipShape = flatShape ?? {
        x: CARD_BIN_SHAPE_INSET,
        y: CARD_BIN_SHAPE_INSET,
        width: CARD_BIN_SHAPE_SIZE,
        height: CARD_BIN_SHAPE_SIZE,
    };
    return (
        <>
            <CardBinLevelClipDef
                clipId={clipId}
                fillRatio={fillRatio}
                shapeX={clipShape.x}
                shapeY={clipShape.y}
                shapeWidth={clipShape.width}
                shapeHeight={clipShape.height}
            />
            {isFlatHouse ? (
                <>
                    <rect
                        x={flatShape.x}
                        y={flatShape.y}
                        width={flatShape.width}
                        height={flatShape.height}
                        fill="#3b3e44"
                    />
                    {fillRatio != null && fillRatio > 0 ? (
                        <rect
                            x={flatShape.x}
                            y={flatShape.y}
                            width={flatShape.width}
                            height={flatShape.height}
                            fill={global.BIN_LEVEL_SITE_CARD_FILL}
                            clipPath={`url(#${clipId})`}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    <circle r={CARD_BIN_RADIUS} cx={CARD_BIN_CENTER} cy={CARD_BIN_CENTER} fill="#3b3e44" />
                    {fillRatio != null && fillRatio > 0 ? (
                        <circle
                            r={CARD_BIN_RADIUS}
                            cx={CARD_BIN_CENTER}
                            cy={CARD_BIN_CENTER}
                            fill={global.BIN_LEVEL_SITE_CARD_FILL}
                            clipPath={`url(#${clipId})`}
                        />
                    ) : null}
                </>
            )}
        </>
    );
};

const CardBinGradeRing = ({ isFlatHouse, binGrade }) => {
    if (binGrade == null) return null;
    if (isFlatHouse) {
        const { x, y, width, height } = cardBinFlatHouseShape();
        const pathD = cardBinRectGradePath(x, y, width, height);
        const perimeter = cardBinRectGradePerimeter(width, height);
        const greenLen = perimeter * binGrade / 100;
        return (
            <>
                {binGrade < 100 ? (
                    <path
                        d={pathD}
                        fill="none"
                        stroke="#990000"
                        strokeWidth={CARD_BIN_GRADE_STROKE}
                    />
                ) : null}
                {binGrade >= 100 ? (
                    <path
                        d={pathD}
                        fill="none"
                        stroke="#4a7c59"
                        strokeWidth={CARD_BIN_GRADE_STROKE}
                        style={{ transition: "all 0.3s ease" }}
                    />
                ) : (
                    <path
                        d={pathD}
                        fill="none"
                        stroke="#4a7c59"
                        strokeWidth={CARD_BIN_GRADE_STROKE}
                        strokeLinecap="square"
                        strokeDasharray={`${greenLen} ${perimeter}`}
                        style={{ transition: "stroke-dasharray 0.3s ease" }}
                    />
                )}
            </>
        );
    }
    const startAngle = Math.PI / 2;
    if (binGrade >= 100) {
        return (
            <circle
                r={CARD_BIN_RADIUS}
                cx={CARD_BIN_CENTER}
                cy={CARD_BIN_CENTER}
                fill="none"
                stroke="#4a7c59"
                strokeWidth={CARD_BIN_GRADE_STROKE}
                style={{ transition: "all 0.3s ease" }}
            />
        );
    }
    const endAngle = startAngle + (2 * Math.PI * binGrade / 100);
    const startX = CARD_BIN_CENTER + CARD_BIN_RADIUS * Math.cos(startAngle);
    const startY = CARD_BIN_CENTER + CARD_BIN_RADIUS * Math.sin(startAngle);
    const endX = CARD_BIN_CENTER + CARD_BIN_RADIUS * Math.cos(endAngle);
    const endY = CARD_BIN_CENTER + CARD_BIN_RADIUS * Math.sin(endAngle);
    const largeArcFlag = binGrade > 50 ? 1 : 0;
    return (
        <>
            <circle
                r={CARD_BIN_RADIUS}
                cx={CARD_BIN_CENTER}
                cy={CARD_BIN_CENTER}
                fill="none"
                stroke="#990000"
                strokeWidth={CARD_BIN_GRADE_STROKE}
            />
            <path
                d={`M ${startX} ${startY} A ${CARD_BIN_RADIUS} ${CARD_BIN_RADIUS} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
                fill="none"
                stroke="#4a7c59"
                strokeWidth={CARD_BIN_GRADE_STROKE}
                strokeLinecap="round"
                style={{ transition: "d 0.3s ease" }}
            />
        </>
    );
};

const CardBinGraphic = ({
    isFlatHouse,
    fillRatio,
    clipId,
    binGrade,
    temperature,
    colorString,
    onClick,
    showGradeRing = false,
}) => {
    const graphicWidth = isFlatHouse ? CARD_BIN_FLAT_HOUSE_GRAPHIC_WIDTH : CARD_BIN_GRAPHIC_SIZE;
    return (
    <MuiMaterials.Box
        sx={{ position: "relative", width: graphicWidth, height: CARD_BIN_GRAPHIC_SIZE, cursor: onClick ? "pointer" : undefined }}
        onClick={onClick}
    >
        <svg height={CARD_BIN_GRAPHIC_SIZE} width={graphicWidth}>
            <CardBinShapeBackground isFlatHouse={isFlatHouse} fillRatio={fillRatio} clipId={clipId} />
        </svg>
        {showGradeRing ? (
            <svg
                height={CARD_BIN_GRAPHIC_SIZE}
                width={graphicWidth}
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    pointerEvents: "none",
                }}
            >
                <CardBinGradeRing isFlatHouse={isFlatHouse} binGrade={binGrade} />
            </svg>
        ) : null}
        <MuiMaterials.Typography
            className="binReading"
            sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                fontSize: "25px",
                fontWeight: 900,
                color: `${colorString}`,
                lineHeight: 1,
                pointerEvents: "none",
            }}
        >
            {temperature}
        </MuiMaterials.Typography>
    </MuiMaterials.Box>
    );
};

export const SiteCard = ({ siteIndex, siteGuid, siteGrade: preloadedSiteGrade, siteIssue: preloadedSiteIssue, isExpanded, onExpandedChange }) => {
    const { token: authToken } = useAuth();
    const navigate = useNavigate();
    const [binElements, setBinElements] = useState(null);
    const [current, setCurrent] = useState(null);
    // Use prop if provided, otherwise fall back to internal state
    const [internalCollapsed, setInternalCollapsed] = useState(true);
    const isCollapsed = isExpanded !== undefined ? !isExpanded : internalCollapsed;
    const [forceUpdate, setForceUpdate] = useState(0);
    const [currentMainSelection, setCurrentMainSelection] = useState("scanTemp");
    const [currentSubSelection, setCurrentSubSelection] = useState("high"); // Temperature sub-tab: High | Average | Low (CO2 uses current/today/yesterday via renderBins)
    const [hmsData, setHmsData] = useState(null);
    const [allDevices, setAllDevices] = useState(new Map()); // Store all devices by deviceId
    const [hmsTimer, setHmsTimer] = useState(null);
    const [hmsLoading, setHmsLoading] = useState(false);
    const hasInitialData = useRef(false); // Track if we've loaded data at least once
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [deviceToReset, setDeviceToReset] = useState(null);
    const [co2ReadModalOpen, setCo2ReadModalOpen] = useState(false);
    const [co2ReadPending, setCo2ReadPending] = useState(null); // { binId, deviceIds }
    const [readingCO2BinId, setReadingCO2BinId] = useState(null); // binId showing spinner
    const co2ReadPollingRef = useRef(null); // { clickTime, deviceIds, binId, siteId, intervalId, initialTimeoutId, timeoutId }
    const [hasCO2Capability, setHasCO2Capability] = useState(null); // null = not checked, true = has CO2, false = no CO2
    const isCheckingCO2 = useRef(false); // Track if we're currently checking CO2 capability
    const hasCO2CapabilityRef = useRef(null); // Ref to track CO2 capability for immediate access
    const currentMainSelectionRef = useRef("scanTemp"); // Ref to track current main selection for async checks
    const currentSubSelectionRef = useRef("high"); // Preserve subSelection for scan vs CO2 async callbacks
    const optimisticallyAssumedCO2Ref = useRef(false); // Track if we optimistically assumed CO2 - if true, NEVER show SalesCard
    const [binGrades, setBinGrades] = useState(new Map()); // Map of binId -> grade
    const [weatherStationHistory, setWeatherStationHistory] = useState(null); // Local temperature and humidity from weather station
    const [weatherStationTimer, setWeatherStationTimer] = useState(null); // Timer for refreshing weather station history
    const weatherStationTimerRef = useRef(null); // Ref to track timer ID to prevent unnecessary restarts
    const [binWeatherStationHistory, setBinWeatherStationHistory] = useState(new Map()); // Map of binId -> {temperature, humidity}
    const [binWeatherStationHistoryUpdate, setBinWeatherStationHistoryUpdate] = useState(0); // Counter to track updates
    /** BinId -> DeviceId[] for WeatherStationDevice rows with WeatherStationDeviceTypeId === 1 (CO2); from GetSiteWeatherStationDevices */
    const [co2Type1DeviceIdsByBin, setCo2Type1DeviceIdsByBin] = useState(null);
    const [siteNavMenuAnchor, setSiteNavMenuAnchor] = useState(null);

    /** Latest BinLevel snapshot rows for this card’s site (Site3d-style fill on bin circles). */
    const [siteBinLevelRows, setSiteBinLevelRows] = useState(null);
    /** dbo.BinDimension geometry for prioritized site capacity vs dashboard Bin coords (loads with BinLevel snapshot). */
    const [siteInventoryBinDimensions, setSiteInventoryBinDimensions] = useState([]);
    /** BTS `Bin` rows from `getSiteInventory` — geometry source the dashboard `fetchUserAttribute.bins` payload omits. */
    const [siteInventoryBins, setSiteInventoryBins] = useState([]);

    // Resolve by siteGuid first so we get the correct site even if siteInfos is reordered/mutated (prevents grade bleeding)
    const siteInfo = global.gw.siteInfos?.find(s => s?.siteGuid === siteGuid) ?? global.gw.siteInfos?.[siteIndex];
    const isCcr = !!siteInfo?.isCcr;
    const lastScanAlertReasons = getLastScanAlertReasons(siteInfo);
    const isBlinking = lastScanAlertReasons.length > 0;
    const lastScanAlertTitle = lastScanAlertTooltip(lastScanAlertReasons);

    // Touch: tap detection for expand, manual scroll for outletContainer (mobile scroll fix)
    const touchStartY = useRef(null);
    const touchStartScrollTop = useRef(null);
    const handledTapRef = useRef(false);
    const touchStartedOnInteractiveRef = useRef(false);

    const isInteractiveCardTarget = (e) => {
        const el = e.target;
        if (!el || typeof el.closest !== 'function') return false;
        return Boolean(
            el.closest(
                '[data-site-card-header-action], button, a, input, textarea, select, [role="button"], .MuiIconButton-root, .MuiMenuItem-root, .MuiMenu-root, .MuiBackdrop-root',
            ),
        );
    };

    const isHeaderToggleTarget = (e) => {
        const el = e.target;
        if (!el || typeof el.closest !== 'function') return false;
        return Boolean(el.closest('[data-site-card-header-toggle]'));
    };

    const collapseSiteCard = () => {
        if (onExpandedChange) {
            onExpandedChange(false);
        } else {
            setInternalCollapsed(true);
        }
    };

    const expandSiteCard = () => {
        if (onExpandedChange) {
            onExpandedChange(true);
        } else {
            setInternalCollapsed(false);
        }
        checkAndStartCO2Timer();
    };

    const consumeTouchHandledClick = (e) => {
        if (!handledTapRef.current) return false;
        handledTapRef.current = false;
        e.stopPropagation();
        return true;
    };

    const handleHeaderAreaClick = (e) => {
        if (isInteractiveCardTarget(e)) return;
        // Mobile/device-mode: touchend already toggled; the compatibility click would immediately undo it.
        if (consumeTouchHandledClick(e)) return;
        e.stopPropagation();
        if (isCollapsed) {
            expandSiteCard();
        } else {
            collapseSiteCard();
        }
    };

    const handleCardTouchStart = (e) => {
        touchStartedOnInteractiveRef.current = isInteractiveCardTarget(e);
        if (touchStartedOnInteractiveRef.current) return;
        touchStartY.current = e.touches[0]?.clientY ?? null;
        handledTapRef.current = false;
        const outletContainer = document.getElementById('outletContainer');
        touchStartScrollTop.current = outletContainer ? outletContainer.scrollTop : 0;
    };
    const handleCardTouchEnd = (e) => {
        if (touchStartedOnInteractiveRef.current) {
            touchStartedOnInteractiveRef.current = false;
            touchStartY.current = null;
            touchStartScrollTop.current = null;
            handledTapRef.current = false;
            return;
        }
        if (touchStartY.current == null) return;
        const endY = e.changedTouches[0]?.clientY ?? touchStartY.current;
        const deltaY = Math.abs(endY - touchStartY.current);
        touchStartY.current = null;
        touchStartScrollTop.current = null;
        // Only treat as tap if movement was minimal (< 10px)
        if (deltaY < 10 && isCollapsed) {
            handledTapRef.current = true;
            if (e.cancelable) e.preventDefault();
            if (onExpandedChange) {
                onExpandedChange(true);
            } else {
                setInternalCollapsed(false);
            }
            checkAndStartCO2Timer();
        } else if (deltaY < 10 && !isCollapsed && isHeaderToggleTarget(e)) {
            handledTapRef.current = true;
            if (e.cancelable) e.preventDefault();
            collapseSiteCard();
        }
    };
    // touchmove on document so we keep receiving events when finger moves off card
    useEffect(() => {
        const onTouchMove = (e) => {
            if (touchStartY.current == null || touchStartScrollTop.current == null) return;
            const currentY = e.touches[0]?.clientY ?? touchStartY.current;
            const deltaY = currentY - touchStartY.current;
            const outletContainer = document.getElementById('outletContainer');
            if (outletContainer && outletContainer.scrollHeight > outletContainer.clientHeight) {
                const maxScroll = outletContainer.scrollHeight - outletContainer.clientHeight;
                outletContainer.scrollTop = Math.max(0, Math.min(maxScroll, touchStartScrollTop.current - deltaY));
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        return () => document.removeEventListener('touchmove', onTouchMove);
    }, []);

    // Sync internal state with prop when it changes
    useEffect(() => {
        if (isExpanded !== undefined) {
            setInternalCollapsed(!isExpanded);
        }
    }, [isExpanded]);

    // Function to check if site has CO2 data and start timer if needed
    const checkAndStartCO2Timer = () => {
        if (global.gw.auth && global.gw.currentWeatherReadings && global.gw.currentWeatherReadings.length > 0) {
            const siteCO2Readings = (global.gw.currentWeatherReadings || []).filter(x => global.normalizeGuidForCompare(x.siteId ?? x.SiteId) === global.normalizeGuidForCompare(siteInfo.siteGuid));
            if (siteCO2Readings.length > 0) {
                // Site has CO2 data, but don't call setAllSitesWeatherStationReadings again
                // as it's already been called and will cause infinite loops
                // console.log('Site has CO2 data, timer already running');
            }
        }
    };

    // Function to fetch HMS data
    const fetchHmsData = useCallback(async () => {
        if (siteInfo.isHms && global.gw.auth) {
            try {
                // Only show loading on initial load (when there's no data yet)
                // Use ref to check if we've loaded data before - this prevents showing loading on subsequent updates
                if (!hasInitialData.current) {
                    setHmsLoading(true);
                }
                // Set the selected site for the API call
                global.gw.selectedSite = {
                    siteGuid: siteInfo.siteGuid,
                    siteName: siteInfo.siteName
                };
                
                const response = await createApiEndpoint().fetchSiteHmsData("direct");
                if (response && response.data && response.data.getSiteHmsData) {
                    let arrayBuffer = global.base64StringToArrayBuffer(response.data.getSiteHmsData);
                    let decompressed = await global.decompress(arrayBuffer, "gzip");
                    let parsedHmsData = JSON.parse(decompressed);
                    // Store the full array of devices
                    const deviceArray = Array.isArray(parsedHmsData) ? parsedHmsData : [parsedHmsData];
                    
                    // Accumulate devices instead of overwriting
                    setAllDevices(prevDevices => {
                        const newDevices = new Map(prevDevices);
                        deviceArray.forEach(device => {
                            newDevices.set(device.deviceid, device);
                        });
                        
                        // Update hmsData with all accumulated devices, sorted by name
                        setHmsData(Array.from(newDevices.values()).sort((a, b) => (a.devicename || '').localeCompare(b.devicename || '')));
                        
                        // Mark that we've loaded initial data
                        if (newDevices.size > 0) {
                            hasInitialData.current = true;
                        }
                        
                        return newDevices;
                    });
                }
            } catch (error) {
                if (global.gw.test) {
                    // console.log("Error fetching data:", error.message);
                }
            } finally {
                setHmsLoading(false);
            }
        }
    }, [siteInfo.isHms, siteInfo.siteGuid, siteInfo.siteName]);

    // Function to open reset confirmation modal
    const handleOpenResetModal = useCallback((deviceName) => {
        setDeviceToReset(deviceName);
        setResetModalOpen(true);
    }, []);

    // Function to close reset confirmation modal
    const handleCloseResetModal = useCallback(() => {
        setResetModalOpen(false);
        setDeviceToReset(null);
    }, []);

    // Function to reset a multiface device
    const handleResetDevice = useCallback(async () => {
        if (!global.gw.auth || !deviceToReset) {
            return;
        }

        try {
            // Set the selected site for the API call
            global.gw.selectedSite = {
                siteGuid: siteInfo.siteGuid,
                siteName: siteInfo.siteName
            };

            let json = {
                deviceName: deviceToReset
            }
            
            const response = await createApiEndpoint().resetMultifaceDevice("direct", siteInfo.siteGuid, json);
            if (response) {
                // Close modal and refresh the data after reset
                handleCloseResetModal();
                fetchHmsData();
            }
        } catch (error) {
            if (global.gw.test) {
                // console.log("Error resetting device:", error.message);
            }
        }
    }, [siteInfo.siteGuid, siteInfo.siteName, deviceToReset, fetchHmsData, handleCloseResetModal]);

    // Open CO2 read confirmation modal
    const handleOpenCO2ReadModal = useCallback((binId, deviceIds, e) => {
        if (e) e.stopPropagation();
        if (!deviceIds || deviceIds.length === 0) return;
        setCo2ReadPending({ binId, deviceIds });
        setCo2ReadModalOpen(true);
    }, []);

    // Close CO2 read modal without sending
    const handleCloseCO2ReadModal = useCallback(() => {
        setCo2ReadModalOpen(false);
        setCo2ReadPending(null);
    }, []);

    // Confirm and send read command to CO2 sensor devices; poll every 20s until we get updated values
    const handleConfirmCO2Read = useCallback(async () => {
        if (!co2ReadPending || !global.gw.auth || !siteInfo.siteGuid) return;
        const { binId, deviceIds } = co2ReadPending;
        setCo2ReadModalOpen(false);
        setCo2ReadPending(null);
        setReadingCO2BinId(binId);
        const clickTime = Date.now();
        const siteId = siteInfo.siteGuid;
        const normalizeGuid = (a, b) => global.normalizeGuidForCompare(a) === global.normalizeGuidForCompare(b);
        const normalizeBinId = (a, b) => {
            const an = typeof a === 'number' ? a : parseInt(a, 10);
            const bn = typeof b === 'number' ? b : parseInt(b, 10);
            return !isNaN(an) && !isNaN(bn) && an === bn;
        };
        const checkForUpdates = () => {
            const readings = global.gw.currentWeatherReadings || [];
            const relevant = readings.filter(r => {
                const matchSite = normalizeGuid(r.siteId ?? r.SiteId ?? r.siteGuid ?? r.SiteGuid, siteId);
                const matchBin = normalizeBinId(r.binId ?? r.BinId, binId);
                const matchDevice = deviceIds.includes(r.DeviceId ?? r.deviceId);
                const hasEui = (r.DeviceEui ?? r.deviceEui) !== null;
                return matchSite && matchBin && matchDevice && hasEui;
            });
            const hasNewer = relevant.some(r => {
                const dr = r.DateRead ?? r.dateRead;
                if (!dr) return false;
                const t = typeof dr === 'string' ? new Date(dr).getTime() : (dr instanceof Date ? dr.getTime() : dr);
                return t > clickTime;
            });
            return hasNewer;
        };
        const stopPolling = () => {
            const ref = co2ReadPollingRef.current;
            if (ref?.intervalId) clearInterval(ref.intervalId);
            if (ref?.initialTimeoutId) clearTimeout(ref.initialTimeoutId);
            if (ref?.timeoutId) clearTimeout(ref.timeoutId);
            if (ref?.onUpdate) window.removeEventListener('grainwave-weather-readings-updated', ref.onUpdate);
            co2ReadPollingRef.current = null;
            setReadingCO2BinId(null);
        };
        const onUpdate = () => {
            if (!co2ReadPollingRef.current) return;
            if (checkForUpdates()) stopPolling();
        };
        co2ReadPollingRef.current = { clickTime, deviceIds, binId, siteId, intervalId: null, onUpdate };
        window.addEventListener('grainwave-weather-readings-updated', onUpdate);
        try {
            global.gw.selectedSite = { siteGuid: siteInfo.siteGuid, siteName: siteInfo.siteName };
            await createApiEndpoint().resetMultifaceDevice("direct", siteInfo.siteGuid, { deviceIds, command: "read" });
            const startInterval = () => setInterval(() => {
                if (!co2ReadPollingRef.current) return;
                global.setAllSitesWeatherStationReadings?.();
            }, 5000);
            const initialTimeoutId = setTimeout(() => {
                if (!co2ReadPollingRef.current) return;
                co2ReadPollingRef.current.initialTimeoutId = null;
                co2ReadPollingRef.current.intervalId = startInterval();
                global.setAllSitesWeatherStationReadings?.();
            }, 20000);
            co2ReadPollingRef.current.initialTimeoutId = initialTimeoutId;
            co2ReadPollingRef.current.timeoutId = setTimeout(() => { if (co2ReadPollingRef.current) stopPolling(); }, 120000);
        } catch (err) {
            stopPolling();
            if (global.gw.test) { /* console.log */ }
        }
    }, [siteInfo.siteGuid, siteInfo.siteName, co2ReadPending]);

    // Function to fetch weather station history (local temperature and humidity)
    const fetchWeatherStationHistory = useCallback(async () => {
        if (!global.gw.auth || !siteInfo.siteGuid) {
            return;
        }

        try {
            // Default JSON parameters
            const jsonRequest = {
                dateStart: null,
                dateEnd: null,
                gatewayId: null,
                deviceId: null,
                binId: null
            };

            const response = await createApiEndpoint().fetchSiteWeatherStationHistory("direct", siteInfo.siteGuid, jsonRequest);
            
            if (response && response.data && response.data.getSiteWeatherStationHistory) {
                try {
                    // Decompress the response
                    let arrayBuffer = global.base64StringToArrayBuffer(response.data.getSiteWeatherStationHistory);
                    let decompressed = await global.decompress(arrayBuffer, "gzip");
                    let responseData = JSON.parse(decompressed);
                    
                    // Check if we have data
                    if (responseData && Array.isArray(responseData) && responseData.length > 0) {
                        // Separate readings by BinId (null vs non-null) for site-level and bin-specific display
                        const readingsWithoutBin = []; // Readings with BinId = null
                        const readingsByBin = new Map(); // Map of binId -> array of readings
                        
                        responseData.forEach(reading => {
                            // Get BinId field - handle various field name cases
                            const binId = reading?.BinId ?? reading?.binId ?? 
                                        reading?.BinID ?? reading?.binID ??
                                        reading?.Bin ?? reading?.bin ?? null;
                            
                            // Separate readings by BinId
                            if (binId == null) {
                                // Reading without BinId - for site-level display
                                readingsWithoutBin.push(reading);
                            } else {
                                // Reading with BinId - for bin-specific display
                                const numericBinId = typeof binId === 'number' ? binId : parseInt(binId, 10);
                                if (!isNaN(numericBinId)) {
                                    if (!readingsByBin.has(numericBinId)) {
                                        readingsByBin.set(numericBinId, []);
                                    }
                                    readingsByBin.get(numericBinId).push(reading);
                                }
                            }
                        });
                        
                        // Process site-level reading: ONLY use records with BinId = null
                        // Readings with BinId values will only go to their respective bins
                        let latestSiteReading = null;
                        let latestSiteDate = null;
                        
                        // Only process readings with BinId = null for site-level display
                        if (readingsWithoutBin.length > 0) {
                            readingsWithoutBin.forEach(reading => {
                                // Get DateRead field - handle various field name cases
                                const dateRead = reading?.DateRead ?? reading?.dateRead ?? 
                                               reading?.Date ?? reading?.date ??
                                               reading?.DateReadTime ?? reading?.dateReadTime ??
                                               reading?.ReadingDate ?? reading?.readingDate ?? null;
                                
                                if (dateRead != null) {
                                    try {
                                        const readingDate = new Date(dateRead);
                                        if (!isNaN(readingDate.getTime())) {
                                            // Valid date - check if this is the latest from BinId = null records
                                            if (latestSiteDate === null || readingDate > latestSiteDate) {
                                                latestSiteDate = readingDate;
                                                latestSiteReading = reading;
                                            }
                                        }
                                    } catch (dateError) {
                                        // Invalid date format, skip this reading
                                    }
                                }
                            });
                            
                            // If no DateRead found in BinId = null records, fall back to last item
                            if (!latestSiteReading && readingsWithoutBin.length > 0) {
                                latestSiteReading = readingsWithoutBin[readingsWithoutBin.length - 1];
                                // Try to get DateRead from this reading
                                if (latestSiteReading && !latestSiteDate) {
                                    const dateRead = latestSiteReading?.DateRead ?? latestSiteReading?.dateRead ?? 
                                                   latestSiteReading?.Date ?? latestSiteReading?.date ??
                                                   latestSiteReading?.DateReadTime ?? latestSiteReading?.dateReadTime ??
                                                   latestSiteReading?.ReadingDate ?? latestSiteReading?.readingDate ?? null;
                                    if (dateRead != null) {
                                        try {
                                            const readingDate = new Date(dateRead);
                                            if (!isNaN(readingDate.getTime())) {
                                                latestSiteDate = readingDate;
                                            }
                                        } catch (dateError) {
                                            // Invalid date format
                                        }
                                    }
                                }
                            }
                        }
                        // If no readings with BinId = null exist, latestSiteReading will remain null
                        // and weatherStationHistory will be set to null (using default site values)
                        // Readings with BinId values will only be displayed on their respective bins
                        
                        // Check if site-level reading is stale (more than 1 hour old)
                        // If no date found, consider it stale
                        let isSiteDataStale = true;
                        if (latestSiteReading && latestSiteDate) {
                            const oneHourAgo = new Date();
                            oneHourAgo.setHours(oneHourAgo.getHours() - 1);
                            isSiteDataStale = latestSiteDate < oneHourAgo;
                        }
                        
                        // Extract temperature and humidity for site-level display - handle various field name cases
                        const siteTemp = latestSiteReading?.Temperature ?? latestSiteReading?.temperature ?? 
                                        latestSiteReading?.Temp ?? latestSiteReading?.temp ?? null;
                        const siteHumidity = latestSiteReading?.Humidity ?? latestSiteReading?.humidity ?? 
                                           latestSiteReading?.Hum ?? latestSiteReading?.hum ?? null;
                        
                        // Only set weather station history if data exists and is not stale
                        if (!isSiteDataStale && (siteTemp != null || siteHumidity != null)) {
                            setWeatherStationHistory({
                                temperature: siteTemp,
                                humidity: siteHumidity
                            });
                        } else {
                            setWeatherStationHistory(null);
                        }
                        
                        // Process bin-level readings (with BinId) for bin-specific display
                        const binHistoryMap = new Map();
                        readingsByBin.forEach((readings, binId) => {
                            // Find the latest reading for this bin
                            let latestReading = null;
                            let latestDate = null;
                            
                            readings.forEach(reading => {
                                // Get DateRead field - handle various field name cases
                                const dateRead = reading?.DateRead ?? reading?.dateRead ?? 
                                               reading?.Date ?? reading?.date ??
                                               reading?.DateReadTime ?? reading?.dateReadTime ??
                                               reading?.ReadingDate ?? reading?.readingDate ?? null;
                                
                                if (dateRead != null) {
                                    try {
                                        const readingDate = new Date(dateRead);
                                        if (!isNaN(readingDate.getTime())) {
                                            // Valid date
                                            if (latestDate === null || readingDate > latestDate) {
                                                latestDate = readingDate;
                                                latestReading = reading;
                                            }
                                        }
                                    } catch (dateError) {
                                        // Invalid date format, skip this reading
                                    }
                                }
                            });
                            
                            // If no DateRead found, fall back to last item in array
                            if (!latestReading && readings.length > 0) {
                                latestReading = readings[readings.length - 1];
                                // If we had to fall back, try to get DateRead from this reading
                                if (latestReading && !latestDate) {
                                    const dateRead = latestReading?.DateRead ?? latestReading?.dateRead ?? 
                                                   latestReading?.Date ?? latestReading?.date ??
                                                   latestReading?.DateReadTime ?? latestReading?.dateReadTime ??
                                                   latestReading?.ReadingDate ?? latestReading?.readingDate ?? null;
                                    if (dateRead != null) {
                                        try {
                                            const readingDate = new Date(dateRead);
                                            if (!isNaN(readingDate.getTime())) {
                                                latestDate = readingDate;
                                            }
                                        } catch (dateError) {
                                            // Invalid date format
                                        }
                                    }
                                }
                            }
                            
                            // Check if bin reading is stale (more than 1 hour old)
                            // If no date found, consider it stale
                            let isBinDataStale = true;
                            if (latestReading && latestDate) {
                                const oneHourAgo = new Date();
                                oneHourAgo.setHours(oneHourAgo.getHours() - 1);
                                isBinDataStale = latestDate < oneHourAgo;
                            }
                            
                            // Extract temperature and humidity - handle various field name cases
                            const temp = latestReading?.Temperature ?? latestReading?.temperature ?? 
                                        latestReading?.Temp ?? latestReading?.temp ?? null;
                            const humidity = latestReading?.Humidity ?? latestReading?.humidity ?? 
                                           latestReading?.Hum ?? latestReading?.hum ?? null;
                            
                            // Add to bin history map if data exists (even if stale, we'll show N/A)
                            if (temp != null || humidity != null) {
                                binHistoryMap.set(binId, {
                                    temperature: temp,
                                    humidity: humidity,
                                    isStale: isBinDataStale
                                });
                            }
                        });
                        
                        setBinWeatherStationHistory(binHistoryMap);
                        setBinWeatherStationHistoryUpdate(prev => prev + 1); // Trigger re-render
                    } else {
                        setWeatherStationHistory(null);
                        setBinWeatherStationHistory(new Map());
                        setBinWeatherStationHistoryUpdate(0);
                    }
                } catch (parseError) {
                    if (global.gw.test) {
                        // console.log("Error parsing weather station history:", parseError.message);
                    }
                    setWeatherStationHistory(null);
                }
            } else {
                setWeatherStationHistory(null);
            }
        } catch (error) {
            if (global.gw.test) {
                // console.log("Error fetching weather station history:", error.message);
            }
            setWeatherStationHistory(null);
        }
    }, [siteInfo.siteGuid, siteInfo.siteName]);

    // Function to start weather station history timer
    const startWeatherStationTimer = useCallback(() => {
        // Don't start if timer already exists
        if (weatherStationTimerRef.current) {
            return;
        }
        
        // Fetch data immediately
        fetchWeatherStationHistory();
        
        // Set up 30-second interval
        const timer = setInterval(() => {
            fetchWeatherStationHistory();
        }, 30000);
        
        weatherStationTimerRef.current = timer;
        setWeatherStationTimer(timer);
    }, [fetchWeatherStationHistory]);

    // Function to stop weather station history timer
    const stopWeatherStationTimer = useCallback(() => {
        if (weatherStationTimerRef.current) {
            clearInterval(weatherStationTimerRef.current);
            weatherStationTimerRef.current = null;
            setWeatherStationTimer(null);
        }
    }, []);

    // Function to start HMS data timer
    const startHmsTimer = useCallback(() => {
        if (siteInfo.isHms && !hmsTimer) {
            // Fetch data immediately
            fetchHmsData();
            
            // Set up 30-second interval
            const timer = setInterval(() => {
                fetchHmsData();
            }, 10000);
            
            setHmsTimer(timer);
        } else {
        }
    }, [siteInfo.isHms, siteInfo.siteName, hmsTimer]);

    // Function to stop HMS data timer
    const stopHmsTimer = useCallback(() => {
        if (hmsTimer) {
            clearInterval(hmsTimer);
            setHmsTimer(null);
        }
        // Clear accumulated devices when stopping timer
        setAllDevices(new Map());
        setHmsData(null);
    }, [hmsTimer, siteInfo.siteName]);

    // Initial render of bin elements
    useEffect(() => {
        // Default to scan temperatures (High), not CO2 — matches clicking the "Temperature" tab
        renderBins("scanTemp", "high");
    }, []);

    // HMS data: only while expanded — avoids N concurrent GetSiteHmsData calls for every HMS site on the list
    useEffect(() => {
        if (siteInfo.isHms && !isCollapsed) {
            startHmsTimer();
        }

        return () => {
            stopHmsTimer();
        };
    }, [siteInfo.isHms, isCollapsed, startHmsTimer, stopHmsTimer]);

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            stopHmsTimer();
            stopWeatherStationTimer();
        };
    }, []);

    // Weather history: fetch only when the card is expanded (collapsed cards use siteInfo.currentTemperature / currentHumidity)
    useEffect(() => {
        if (!isCollapsed && !isCcr && global.gw.auth && siteInfo?.siteGuid) {
            fetchWeatherStationHistory();
        }
    }, [isCollapsed, siteInfo?.siteGuid, fetchWeatherStationHistory]);

    useEffect(() => {
        if (siteInfo.isHms || isCcr || typeof document === 'undefined') return;
        const root = document.querySelector(`.siteCard[siteid="${siteInfo.siteGuid}"]`);
        if (!root) return;
        if (isCollapsed) {
            root.querySelector('.scanTemp')?.style?.removeProperty('display');
            root.querySelector('.subCo2')?.style?.removeProperty('display');
        }
    }, [isCollapsed, siteInfo.isHms, siteInfo.siteGuid]);

    // Manage weather station history timer based on expansion state and data availability
    useEffect(() => {
        // Only start timer if site is expanded and has weather station history data (site-level or bin-level)
        const hasSiteData = weatherStationHistory && (weatherStationHistory.temperature != null || weatherStationHistory.humidity != null);
        const hasBinData = binWeatherStationHistory.size > 0;
        const hasData = hasSiteData || hasBinData;
        
        if (!isCollapsed && hasData) {
            // Only start if timer doesn't exist
            if (!weatherStationTimerRef.current) {
                startWeatherStationTimer();
            }
        } else {
            // Stop timer if collapsed or no data
            if (weatherStationTimerRef.current) {
                stopWeatherStationTimer();
            }
        }
        
        return () => {
            if (weatherStationTimerRef.current) {
                stopWeatherStationTimer();
            }
        };
    }, [isCollapsed, !!weatherStationHistory, binWeatherStationHistory.size, startWeatherStationTimer, stopWeatherStationTimer]);

    // Listen for theme changes and re-render bin elements
    useEffect(() => {
        const handleThemeChange = () => {
            // Re-render bin elements with current selections when theme changes
            if (currentMainSelection && currentSubSelection) {
                renderBins(currentMainSelection, currentSubSelection);
            }
        };

        window.addEventListener('themeChanged', handleThemeChange);
        
        return () => {
            window.removeEventListener('themeChanged', handleThemeChange);
        };
    }, [currentMainSelection, currentSubSelection]);

    // Also listen for the global theme change event
    useEffect(() => {
        if (global.gw.themeChanged) {
            // Re-render bin elements when global theme changes
            if (currentMainSelection && currentSubSelection) {
                renderBins(currentMainSelection, currentSubSelection);
            }
        }
    }, [global.gw.themeChanged, currentMainSelection, currentSubSelection]);

    // When expanding, always default to Temperature (High), not CO2 — even if CO2 readings exist or load later.
    useEffect(() => {
        if (siteInfo.isHms || isCcr || isCollapsed) {
            return;
        }

        requestAnimationFrame(() => {
            setTimeout(() => renderBins("scanTemp", "high"), 10);
        });
    }, [isCollapsed, siteInfo.siteGuid, siteInfo.isHms]);

    // renderBins assigns element.style.display for sub-rows; inline display beats MUI sx, so collapsed cards kept showing tabs.
    useEffect(() => {
        if (siteInfo.isHms || isCcr || typeof document === "undefined") return;
        const siteCardEl = document.querySelector(`.siteCard[siteid="${siteInfo.siteGuid}"]`);
        if (!siteCardEl) return;
        const scanEl = siteCardEl.querySelector(".scanTemp");
        const co2El = siteCardEl.querySelector(".subCo2");
        if (!scanEl || !co2El) return;
        if (isCollapsed) {
            scanEl.style.display = "none";
            co2El.style.display = "none";
        } else {
            scanEl.style.removeProperty("display");
            co2El.style.removeProperty("display");
        }
    }, [isCollapsed, siteInfo.siteGuid, siteInfo.isHms, currentMainSelection]);

    // Reset CO2 capability check only when site changes (not when switching tabs)
    useEffect(() => {
        setHasCO2Capability(null);
        hasCO2CapabilityRef.current = null;
        isCheckingCO2.current = false;
        optimisticallyAssumedCO2Ref.current = false; // Reset optimistic assumption when site changes
        hasInitialData.current = false; // Reset initial data flag when site changes
        setCo2Type1DeviceIdsByBin(null);
    }, [siteInfo.siteGuid]);

    // Load CO2-capable weather station devices (type 1) per bin so Read buttons show without current readings
    useEffect(() => {
        if (isCollapsed || siteInfo.isHms || isCcr || !global.gw.auth || !siteInfo?.siteGuid) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await createApiEndpoint().fetchSiteWeatherStationDevices("direct", siteInfo.siteGuid);
                if (cancelled) return;
                if (response?.data?.getSiteWeatherStationDevices) {
                    const arrayBuffer = global.base64StringToArrayBuffer(response.data.getSiteWeatherStationDevices);
                    const decompressed = await global.decompress(arrayBuffer, "gzip");
                    const rows = JSON.parse(decompressed);
                    const list = Array.isArray(rows) ? rows : [];
                    const byBin = {};
                    for (const row of list) {
                        const typeRaw = row.WeatherStationDeviceTypeId ?? row.weatherStationDeviceTypeId;
                        const typeId = typeRaw == null ? NaN : Number(typeRaw);
                        if (typeId !== 1) continue;
                        const bidRaw = row.BinId ?? row.binId;
                        if (bidRaw == null) continue;
                        const bid = typeof bidRaw === "number" ? bidRaw : parseInt(bidRaw, 10);
                        if (isNaN(bid)) continue;
                        const devId = row.DeviceId ?? row.deviceId;
                        if (devId == null) continue;
                        const did = typeof devId === "number" ? devId : parseInt(devId, 10);
                        if (!byBin[bid]) byBin[bid] = [];
                        byBin[bid].push(isNaN(did) ? devId : did);
                    }
                    if (!cancelled) setCo2Type1DeviceIdsByBin(byBin);
                } else if (!cancelled) {
                    setCo2Type1DeviceIdsByBin({});
                }
            } catch {
                if (!cancelled) setCo2Type1DeviceIdsByBin({});
            }
        })();
        return () => { cancelled = true; };
    }, [isCollapsed, siteInfo.siteGuid, siteInfo.isHms]);

    useEffect(() => {
        if (
            siteInfo.isHms
            || isCcr
            || !siteInfo?.siteGuid
            || !siteInfo.bins?.length
            || !authToken
            || !global.gw?.email
            || !global.gw?.bolts
        ) {
            return;
        }
        let cancelled = false;
        (async () => {
            const [rows, env] = await Promise.all([
                global.fetchBinLevelSnapshotRowsForSiteGuid(siteInfo.siteGuid),
                global.fetchSiteInventoryEnvelopeForSiteGuid(siteInfo.siteGuid),
            ]);
            if (!cancelled) {
                setSiteBinLevelRows(Array.isArray(rows) ? rows : []);
                setSiteInventoryBinDimensions(Array.isArray(env?.binDimensions) ? env.binDimensions : []);
                setSiteInventoryBins(Array.isArray(env?.bins) ? env.bins : []);
            }
        })();
        return () => { cancelled = true; };
    }, [siteInfo.siteGuid, siteInfo.isHms, siteInfo.bins?.length, authToken]);

    useEffect(() => {
        const onInvSaved = (e) => {
            const g = e.detail?.siteGuid;
            if (!g || global.normalizeGuidForCompare(g) !== global.normalizeGuidForCompare(siteInfo.siteGuid)) return;
            if (siteInfo.isHms || isCcr || !siteInfo.bins?.length) return;
            Promise.all([
                global.fetchBinLevelSnapshotRowsForSiteGuid(siteInfo.siteGuid),
                global.fetchSiteInventoryEnvelopeForSiteGuid(siteInfo.siteGuid),
            ]).then(([rows, env]) => {
                setSiteBinLevelRows(Array.isArray(rows) ? rows : []);
                setSiteInventoryBinDimensions(Array.isArray(env?.binDimensions) ? env.binDimensions : []);
                setSiteInventoryBins(Array.isArray(env?.bins) ? env.bins : []);
            });
        };
        window.addEventListener("grainwave-site-inventory-saved", onInvSaved);
        return () => window.removeEventListener("grainwave-site-inventory-saved", onInvSaved);
    }, [siteInfo.siteGuid, siteInfo.isHms, siteInfo.bins?.length]);

    // Clear bin elements and cached BinLevel rows when the site identity changes
    useEffect(() => {
        setBinElements(null);
        setSiteBinLevelRows(null);
        setSiteInventoryBinDimensions([]);
        setSiteInventoryBins([]);
    }, [siteInfo.siteGuid]);

    // Re-render bins when binGrades changes (after async fetch completes)
    useEffect(() => {
        // Only re-render if we have bins already rendered and we're not collapsed
        // Check if binGrades has been populated (size > 0) and we have current selections
        if (binElements !== null && !isCollapsed && !siteInfo.isHms && !isCcr && binGrades.size > 0 && currentMainSelection) {
            // Re-render with current selections to update bin grades
            // Use a small delay to ensure state is stable
            const timeoutId = setTimeout(() => {
                renderBins(currentMainSelection, currentSubSelection);
            }, 10);
            return () => clearTimeout(timeoutId);
        }
    }, [binGrades.size, currentMainSelection, currentSubSelection, isCollapsed, siteInfo.isHms]);

    // Re-render bins when binWeatherStationHistory changes (after weather station data is fetched)
    useEffect(() => {
        // Only re-render if we have bins already rendered and we're not collapsed
        // Re-render whenever binWeatherStationHistoryUpdate changes (which happens every 30 seconds when data refreshes)
        if (binElements !== null && !isCollapsed && !siteInfo.isHms && !isCcr && currentMainSelection) {
            // Re-render with current selections to update bin weather station data
            // Use a small delay to ensure state is stable
            const timeoutId = setTimeout(() => {
                renderBins(currentMainSelection, currentSubSelection);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [binWeatherStationHistoryUpdate, currentMainSelection, currentSubSelection, isCollapsed, siteInfo.isHms]);

    // Re-render bins when CO2 device registry loads (Read buttons without prior readings)
    useEffect(() => {
        if (binElements !== null && !isCollapsed && !siteInfo.isHms && !isCcr && currentMainSelection) {
            const timeoutId = setTimeout(() => {
                renderBins(currentMainSelection, currentSubSelection);
            }, 10);
            return () => clearTimeout(timeoutId);
        }
    }, [co2Type1DeviceIdsByBin, currentMainSelection, currentSubSelection, isCollapsed, siteInfo.isHms]);

    // Re-render bins when readingCO2BinId changes (to show/hide spinner on Get Current CO2 button)
    useEffect(() => {
        if (binElements !== null && !isCollapsed && !siteInfo.isHms && !isCcr && currentMainSelection) {
            const timeoutId = setTimeout(() => {
                renderBins(currentMainSelection, currentSubSelection);
            }, 10);
            return () => clearTimeout(timeoutId);
        }
    }, [readingCO2BinId]);

    useEffect(() => {
        if (binElements !== null && !isCollapsed && !siteInfo.isHms && !isCcr && currentMainSelection && siteBinLevelRows !== null) {
            const timeoutId = setTimeout(() => {
                renderBins(currentMainSelection, currentSubSelection);
            }, 10);
            return () => clearTimeout(timeoutId);
        }
    }, [siteBinLevelRows, currentMainSelection, currentSubSelection, isCollapsed, siteInfo.isHms]);

    useEffect(() => {
        if (binElements !== null && !isCollapsed && !siteInfo.isHms && !isCcr && currentMainSelection) {
            const timeoutId = setTimeout(() => {
                renderBins(currentMainSelection, currentSubSelection);
            }, 10);
            return () => clearTimeout(timeoutId);
        }
    }, [siteInventoryBinDimensions, siteInventoryBins, currentMainSelection, currentSubSelection, isCollapsed, siteInfo.isHms]);

    // Min height for collapsed cards without an inventory table (compact header).
    const collapsedCardMinHeight = () => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
        return isMobile ? "68px" : "84px";
    };

    const siteCardHeaderActionButtonSx = {
        color: global.gw.theme === 'light' ? '#2d2d2d' : '#f4cc8e',
        minWidth: { xs: '36px', sm: '40px', md: '44px' },
        width: { xs: '36px', sm: '40px', md: '44px' },
        height: { xs: '36px', sm: '40px', md: '44px' },
        borderRadius: '50%',
        border: global.gw.theme === 'light'
            ? '1px solid rgba(45, 45, 45, 0.3)'
            : '1px solid rgba(244, 204, 142, 0.2)',
        background: global.gw.theme === 'light'
            ? 'linear-gradient(135deg, rgba(45, 45, 45, 0.1) 0%, rgba(45, 45, 45, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(244, 204, 142, 0.1) 0%, rgba(244, 204, 142, 0.05) 100%)',
        flexShrink: 0,
        marginRight: 0,
        padding: 0,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: global.gw.theme === 'light'
            ? '0 2px 8px rgba(0, 0, 0, 0.1)'
            : '0 2px 8px rgba(0, 0, 0, 0.2)',
        '&:hover': {
            background: global.gw.theme === 'light'
                ? 'linear-gradient(135deg, rgba(45, 45, 45, 0.15) 0%, rgba(45, 45, 45, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(244, 204, 142, 0.15) 0%, rgba(244, 204, 142, 0.08) 100%)',
            transform: 'scale(1.05)',
            boxShadow: global.gw.theme === 'light'
                ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                : '0 4px 12px rgba(0, 0, 0, 0.3)',
            border: global.gw.theme === 'light'
                ? '1px solid rgba(45, 45, 45, 0.4)'
                : '1px solid rgba(244, 204, 142, 0.3)',
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
    };

    const siteCardNavMenuItemSx = {
        fontSize: "13px",
        fontWeight: 600,
        color: global.gw.theme === "light" ? "#2d2d2d" : "#f4cc8e",
        "&:hover": {
            backgroundColor:
                global.gw.theme === "light"
                    ? "rgba(0, 0, 0, 0.06)"
                    : "rgba(244, 204, 142, 0.12)",
        },
        "& sub": { fontSize: "0.7em", verticalAlign: "baseline" },
    };

    const handleSiteNavMenuOpen = (e) => {
        e.stopPropagation();
        setSiteNavMenuAnchor(e.currentTarget);
    };

    const handleSiteNavMenuClose = (e) => {
        if (e) e.stopPropagation();
        setSiteNavMenuAnchor(null);
    };

    const runSiteNavAction = (action) => (e) => {
        e.stopPropagation();
        setSiteNavMenuAnchor(null);
        action(e);
    };

    


    // Helper function to save scroll position before navigation
    const saveScrollPosition = () => {
        // Check both window and outletContainer scroll positions
        const outletContainer = document.getElementById('outletContainer');
        const scrollPosition1 = window.pageYOffset || 0;
        const scrollPosition2 = document.documentElement.scrollTop || 0;
        const scrollPosition3 = document.body.scrollTop || 0;
        const scrollPosition4 = outletContainer ? outletContainer.scrollTop : 0;
        // Use the maximum value to ensure we capture the true scroll position
        const scrollPosition = Math.max(scrollPosition1, scrollPosition2, scrollPosition3, scrollPosition4);
        
        sessionStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
        localStorage.setItem('grainwave_customersites_scroll', scrollPosition.toString());
        // Set flag to indicate we're navigating away (not refreshing)
        sessionStorage.setItem('grainwave_customersites_navigated_away', 'true');
    };

    const site3d = async (e) => {
        const clickStartTime = performance.now();
        // console.log('[PERF] SiteCard: Site 3D button clicked');
        saveScrollPosition();
        
        // Cancel any ongoing CustomerSites API calls to free up resources for Site3D
        if (global.gw.weatherStationAbortController) {
            global.gw.weatherStationAbortController.abort();
            global.gw.weatherStationAbortController = null;
        }
        // Clear the dashboard interval to stop background API calls
        if (global.gw.dashboardInterval) {
            clearInterval(global.gw.dashboardInterval);
            global.gw.dashboardInterval = null;
        }
        
        siteSelected(e);
        markSite3dPinned();
        navigate("/site3d", {
            state: {
                siteGuid: siteInfo.siteGuid,
                autoLoad: true,
            },
        });
    };

    const temperatureComparison = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        openSiteWindow(SITE_WINDOW_IDS.comparison, siteInfo.siteGuid);
    };

    const cableHealth = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        navigate("/cableHealth", {
            state: {
                siteGuid: siteInfo.siteGuid,
                autoLoad: true,
            },
        });
    };

    const co2 = async (e, binId, binName) => {
        saveScrollPosition();
        siteSelected(e);
        if (binId && binName) {
            global.gw.selectedSite.carbonDioxideBinId = binId;
            global.gw.selectedSite.carbonDioxideBinName = binName;
        }
        else {
            global.gw.selectedSite.carbonDioxideBinId = null;
            global.gw.selectedSite.carbonDioxideBinName = null;
        }
        openSiteWindow(SITE_WINDOW_IDS.co2, siteInfo.siteGuid);
    };

    const temperatureHistory = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        openSiteWindow(SITE_WINDOW_IDS.history, siteInfo.siteGuid);
    };

    const scan = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        openSiteWindow(SITE_WINDOW_IDS.scan, siteInfo.siteGuid);
    };

    const devices = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        openSiteWindow(SITE_WINDOW_IDS.devices, siteInfo.siteGuid);
    };

    const inventory = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        openSiteWindow(SITE_WINDOW_IDS.inventory, siteInfo.siteGuid);
    };

    const siteIssues = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        window.dispatchEvent(
            new CustomEvent("grainwave-open-site-issues", {
                detail: { siteGuid: siteInfo.siteGuid },
            }),
        );
    };

    const siteLayout = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        navigate("/siteLayout", {
            state: {
                loadSiteForLayout: true,
                siteGuid: siteInfo.siteGuid,
            },
        });
    };

    const openCcr = (e, assetTag) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        saveScrollPosition();
        siteSelected(e);
        const tag = typeof assetTag === "string" ? assetTag.trim() : "";
        navigate(tag
            ? `/ccr/${siteInfo.siteGuid}/${encodeURIComponent(tag)}`
            : `/ccr/${siteInfo.siteGuid}`);
    };

    const siteSetup = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        navigate("/siteSetup", {
            state: {
                siteGuid: siteInfo.siteGuid,
                autoLoad: true,
            },
        });
    };

    const siteVersioning = async (e) => {
        saveScrollPosition();
        siteSelected(e);
        window.dispatchEvent(
            new CustomEvent("grainwave-open-site-versioning", {
                detail: { siteGuid: siteInfo.siteGuid },
            }),
        );
    };

    const bin3d = async (e) => {
        saveScrollPosition();
        if (global.gw.weatherStationAbortController) {
            global.gw.weatherStationAbortController.abort();
            global.gw.weatherStationAbortController = null;
        }
        if (global.gw.dashboardInterval) {
            clearInterval(global.gw.dashboardInterval);
            global.gw.dashboardInterval = null;
        }
        siteSelected(e);
        navigate("/bin", {
            state: {
                siteGuid: siteInfo.siteGuid,
                autoLoad: true,
            },
        });
    };

    const handleBinClick = async (e, binId, binName) => {
        saveScrollPosition();
        siteSelected(e);
        global.gw.selectedSite.selectedBinId = binId;
        global.gw.selectedSite.selectedBinName = binName;
        
        // Call the bin change handler to reset cable selection if it exists
        if (global.gw.selectedSite.onBinChange) {
            global.gw.selectedSite.onBinChange();
        }
        
        navigate("/bin");
    };  

    const siteSelected = async (e) => {
        e.preventDefault();

        if (global.gw.selectedSite.refreshId) {
            clearTimeout(global.gw.selectedSite.refreshId);
        }

        global.gw.selectedSite = new global.Site();
        global.gw.selectedSite.siteIndex = siteInfo.siteIndex;
        global.gw.selectedSite.siteGuid = siteInfo.siteGuid;
        global.gw.selectedSite.siteName = siteInfo.siteName;
        global.gw.selectedSite.lastScan = siteInfo.lastScan;
        // Keep comparison/report recipients available before fetchUserSite fills Main.
        const emailReportTo = siteInfo.emailReportTo ?? siteInfo.EmailReportTo ?? '';
        global.gw.selectedSite.emailReportTo = emailReportTo;
        global.gw.selectedSite.main = { emailReportTo };
        
        // Save selected site to localStorage for persistence across page refreshes
        saveSelectedSite();

        //  Flash
        const flashingElements = document.getElementsByClassName("flashing");
        if (flashingElements && flashingElements.length > 0) {
            for (var i = 0; i < flashingElements.length; i++) {
                const element = flashingElements[i];
                element.classList.remove('flashing');
                clearInterval(intervalIds[i]);
            }
        }

        clearInterval(global.gw.dashboardInterval);
        global.gw.dashboardInterval = null;
    };

    let intervalIds = [];

    useEffect(() => {
        if (current === null) {
            renderBins("scanTemp", "high");
        }

        window.scrollTo(0, 100);
    }, [current]);

    // Listen for theme changes to re-render
    useEffect(() => {
        const handleThemeChange = () => {
            // Force re-render when theme changes
            setForceUpdate(Date.now());
        };
        
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const renderBins = (mainSelection, subSelection) => {
        // Store current selections for theme change handling
        setCurrentMainSelection(mainSelection);
        currentMainSelectionRef.current = mainSelection; // Update ref for async checks
        setCurrentSubSelection(subSelection);
        currentSubSelectionRef.current = subSelection; // Preserve for async callbacks (e.g. HasData API)
        global.gw.selectedSite = new global.Site();
        global.gw.selectedSite.siteIndex = siteInfo.siteIndex;

        // Fetch bin grades if site is not HMS
        if (!siteInfo.isHms && !isCcr && siteInfo.bins && siteInfo.bins.length > 0) {
            (async () => {
                try {
                    if (!global.gw.selectedSite || global.gw.selectedSite.siteGuid !== siteInfo.siteGuid) {
                        global.gw.selectedSite = new global.Site();
                        global.gw.selectedSite.siteGuid = siteInfo.siteGuid;
                        global.gw.selectedSite.siteIndex = siteInfo.siteIndex;
                        global.gw.selectedSite.siteName = siteInfo.siteName;
                        global.gw.selectedSite.lastScan = siteInfo.lastScan;
                    }

                    // Default to today's date
                    const today = new Date();
                    let jsonRequest = {
                        //date: today.toISOString().split('T')[0]
                        date: null
                    };

                    let binGradesResponse = await createApiEndpoint().fetchBinGrades("direct", jsonRequest, siteInfo.siteGuid);
                    
                    if (binGradesResponse && binGradesResponse.data && binGradesResponse.data.getBinGrades) {
                        try {
                            // Decompress the response
                            let arrayBuffer = global.base64StringToArrayBuffer(binGradesResponse.data.getBinGrades);
                            let decompressed = await global.decompress(arrayBuffer, "gzip");
                            let responseData = JSON.parse(decompressed);
                            
                            const gradesMap = new Map();
                            if (responseData && Array.isArray(responseData)) {
                                responseData.forEach(bg => {
                                    // Convert BinId to number to ensure type consistency with b.binId
                                    const binId = typeof bg.BinId === 'number' ? bg.BinId : parseInt(bg.BinId, 10);
                                    if (!isNaN(binId)) {
                                        const dateRead = bg.DateRead ?? bg.dateRead ?? null;
                                        gradesMap.set(binId, { grade: bg.Grade, averageTimeToRead: bg.AverageTimeToRead, dateRead });
                                    }
                                });
                            }
                            setBinGrades(gradesMap);
                        } catch (parseError) {
                            console.error('Error parsing bin grades response:', parseError);
                            setBinGrades(new Map()); // On parse error, clear grades
                        }
                    } else {
                        setBinGrades(new Map()); // No data, clear grades
                    }
                } catch (error) {
                    console.error('Error fetching bin grades:', error);
                    setBinGrades(new Map()); // On error, clear grades
                }
            })();
        } else {
            setBinGrades(new Map()); // HMS sites or no bins, clear grades
        }
        global.gw.selectedSite.siteGuid = siteInfo.siteGuid;
        global.gw.selectedSite.siteName = siteInfo.siteName;
        global.gw.selectedSite.lastScan = siteInfo.lastScan;

        // For HMS sites, skip the highlighting logic since toggle elements don't exist
        if (siteInfo.isHms || isCcr) {
            return;
        }

        if (mainSelection === "co2" && subSelection === "current") {
            if (global.gw.auth && global.gw.currentWeatherReadings.length === 0) {
                // Only call if we don't have weather data yet
                global.setAllSitesWeatherStationReadings();
            }
        }

        const cardRoot =
            typeof window !== "undefined"
                ? window.document.querySelector(`.siteCard[siteid="${siteInfo.siteGuid}"]`)
                : null;

        let subCo2 = cardRoot ? cardRoot.getElementsByClassName("subCo2") : null;
        let scanTemp = cardRoot ? cardRoot.getElementsByClassName("scanTemp") : null;
        if (!isCollapsed) {
        if (siteInfo && siteInfo.bins && siteInfo.bins.length > 0) {
            if (mainSelection !== "") {
                let showLowBinTemperatures = cardRoot ? cardRoot.getElementsByClassName("showLowBinTemperatures") : null;
                let showAverageBinTemperatures = cardRoot ? cardRoot.getElementsByClassName("showAverageBinTemperatures") : null;
                let showHighBinTemperatures = cardRoot ? cardRoot.getElementsByClassName("showHighBinTemperatures") : null;
                let showCarbonDioxide = cardRoot ? cardRoot.getElementsByClassName("showCarbonDioxide") : null;
                let showScanTemperatures = cardRoot ? cardRoot.getElementsByClassName("showScanTemperatures") : null;
                if (
                    cardRoot &&
                    showLowBinTemperatures && showLowBinTemperatures.length > 0 &&
                    showAverageBinTemperatures && showAverageBinTemperatures.length > 0 &&
                    showHighBinTemperatures && showHighBinTemperatures.length > 0 &&
                    showScanTemperatures && showScanTemperatures.length > 0 &&
                    showCarbonDioxide && showCarbonDioxide.length > 0
                ) {
                    const scanTempEl = scanTemp && scanTemp.length ? scanTemp[0] : null;
                    const subCo2El = subCo2 && subCo2.length ? subCo2[0] : null;
                    const hasTabs = !!(scanTempEl && subCo2El);

                    if (hasTabs && mainSelection === "co2") {
                        if (showScanTemperatures[0]) {
                            showScanTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                        }
                        if (showCarbonDioxide[0]) {
                            showCarbonDioxide[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                        }
                        scanTempEl.style.display = "none";
                        if (subCo2El.childNodes && subCo2El.childNodes[0]) {
                            subCo2El.style.display = "flex";
                            subCo2El.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                        }
                    }
                    else if (hasTabs && mainSelection === "scanTemp") {
                        if (showScanTemperatures[0]) {
                            showScanTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                        }
                        if (showCarbonDioxide[0]) {
                            showCarbonDioxide[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                        }
                        if (subSelection === "average") {
                            subCo2El.style.display = "none";
                            if (scanTempEl.childNodes) {
                                scanTempEl.style.display = "flex";
                                if (scanTempEl.childNodes[0]) {
                                    scanTempEl.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                                }
                                if (scanTempEl.childNodes[2]) {
                                    scanTempEl.childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                }
                                if (scanTempEl.childNodes[4]) {
                                    scanTempEl.childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                }
                            }
                            if (showAverageBinTemperatures[0]) {
                                showAverageBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (showHighBinTemperatures[0]) {
                                showHighBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (showLowBinTemperatures[0]) {
                                showLowBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        }
                        else if (subSelection === "low") {
                            subCo2El.style.display = "none";
                            if (scanTempEl.childNodes) {
                                scanTempEl.style.display = "flex";
                                if (scanTempEl.childNodes[0]) {
                                    scanTempEl.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                                }
                                if (scanTempEl.childNodes[2]) {
                                    scanTempEl.childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                }
                                if (scanTempEl.childNodes[4]) {
                                    scanTempEl.childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                }
                            }
                            if (showLowBinTemperatures[0]) {
                                showLowBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (showHighBinTemperatures[0]) {
                                showHighBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (showAverageBinTemperatures[0]) {
                                showAverageBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        }
                        else {
                            subCo2El.style.display = "none";
                            if (scanTempEl.childNodes) {
                                scanTempEl.style.display = "flex";
                                if (scanTempEl.childNodes[0]) {
                                    scanTempEl.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                                }
                                if (scanTempEl.childNodes[2]) {
                                    scanTempEl.childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                }
                                if (scanTempEl.childNodes[4]) {
                                    scanTempEl.childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                }
                            }
                            if (showHighBinTemperatures[0]) {
                                showHighBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (showLowBinTemperatures[0]) {
                                showLowBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (showAverageBinTemperatures[0]) {
                                showAverageBinTemperatures[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        }
                    }
                }
            }
        }
        
        // Always apply main tab highlighting (Temperature/CO2) regardless of bins
        // This ensures tabs are highlighted correctly when clicked
        if (mainSelection !== "") {
            // Find elements by siteid attribute for more reliable matching
            const temperatureElement = window.document.querySelector(`.showScanTemperatures[siteid="${siteInfo.siteGuid}"]`);
            const co2Element = window.document.querySelector(`.showCarbonDioxide[siteid="${siteInfo.siteGuid}"]`);
            
            // Find the site card container to locate sub-tabs within this specific card
            const siteCardElement = cardRoot ?? window.document.querySelector(`.siteCard[siteid="${siteInfo.siteGuid}"]`);
            
            // Get sub-tab elements within this specific site card
            const scanTempElement = siteCardElement ? siteCardElement.querySelector('.scanTemp') : null;
            const subCo2Element = siteCardElement ? siteCardElement.querySelector('.subCo2') : null;
            
            if (temperatureElement && co2Element) {
                if (mainSelection === "co2") {
                    // CO2 is active - highlight CO2, dim Temperature
                    temperatureElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                    co2Element.style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                    // Show CO2 sub-tabs (Current | 24h | 48h), hide Temperature sub-tabs
                    if (scanTempElement) {
                        scanTempElement.style.display = "none";
                    }
                    if (subCo2Element && subCo2Element.childNodes) {
                        subCo2Element.style.display = "flex";
                        // Highlight based on subSelection: "current" (default), "today" (24h), or "yesterday" (48h)
                        if (subSelection === "current") {
                            // Highlight "Current" by default, dim "24h" and "48h"
                            if (subCo2Element.childNodes[0]) {
                                subCo2Element.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (subCo2Element.childNodes[2]) {
                                subCo2Element.childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (subCo2Element.childNodes[4]) {
                                subCo2Element.childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        } else if (subSelection === "today") {
                            // Highlight "24h", dim "Current" and "48h"
                            if (subCo2Element.childNodes[0]) {
                                subCo2Element.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (subCo2Element.childNodes[2]) {
                                subCo2Element.childNodes[2].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (subCo2Element.childNodes[4]) {
                                subCo2Element.childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        } else if (subSelection === "yesterday") {
                            // Highlight "48h", dim "Current" and "24h"
                            if (subCo2Element.childNodes[0]) {
                                subCo2Element.childNodes[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (subCo2Element.childNodes[2]) {
                                subCo2Element.childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (subCo2Element.childNodes[4]) {
                                subCo2Element.childNodes[4].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                        }
                    }
                }
                else if (mainSelection === "scanTemp") {
                    // Temperature is active - highlight Temperature, dim CO2
                    temperatureElement.style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                    co2Element.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                    // Show Temperature sub-tabs (High | Average | Low), hide CO2 sub-tabs
                    if (subCo2Element) {
                        subCo2Element.style.display = "none";
                    }
                    if (scanTempElement) {
                        scanTempElement.style.display = "flex";
                        // Find Temperature sub-tab elements within this specific site card
                        const highElement = siteCardElement ? siteCardElement.querySelector('.showHighBinTemperatures') : null;
                        const averageElement = siteCardElement ? siteCardElement.querySelector('.showAverageBinTemperatures') : null;
                        const lowElement = siteCardElement ? siteCardElement.querySelector('.showLowBinTemperatures') : null;
                        
                        // Highlight based on subSelection: "high" (default), "average", or "low"
                        if (subSelection === "high") {
                            // Highlight "High", dim "Average" and "Low"
                            if (highElement) {
                                highElement.style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (averageElement) {
                                averageElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (lowElement) {
                                lowElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        } else if (subSelection === "average") {
                            // Highlight "Average", dim "High" and "Low"
                            if (highElement) {
                                highElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (averageElement) {
                                averageElement.style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                            if (lowElement) {
                                lowElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                        } else if (subSelection === "low") {
                            // Highlight "Low", dim "High" and "Average"
                            if (highElement) {
                                highElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (averageElement) {
                                averageElement.style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                            }
                            if (lowElement) {
                                lowElement.style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                            }
                        }
                    }
                }
            }
        }
        } else {
            const siteCardCollapsed = window.document.querySelector(`.siteCard[siteid="${siteInfo.siteGuid}"]`);
            if (siteCardCollapsed) {
                const hideScan = siteCardCollapsed.querySelector(".scanTemp");
                const hideCo2 = siteCardCollapsed.querySelector(".subCo2");
                if (hideScan) hideScan.style.display = "none";
                if (hideCo2) hideCo2.style.display = "none";
            }
        }

        if (siteInfo && siteInfo.bins && siteInfo.bins.length > 0) {
            if (mainSelection === "co2") {
                // Sites with bins: ALWAYS show bins on CO2 tab - never show SalesCard
                // SalesCard is for sites without CO2 hardware; sites with bins may have CO2 sensors
                const getApiSiteId = (x) => x.siteId ?? x.SiteId ?? x.siteGuid ?? x.SiteGuid;
                const siteMatches = (x) => global.normalizeGuidForCompare(getApiSiteId(x)) === global.normalizeGuidForCompare(siteInfo.siteGuid);
                let anyCurrentReadings = (global.gw.currentWeatherReadings || []).filter(siteMatches);
                
                // Sites with bins: always show bins (with data or "—" placeholder). Never show SalesCard.
            }

            setBinElements(
                siteInfo.bins.map((b, i) => {
                    let temperature = "";
                    if (mainSelection !== "co2") {
                        temperature = "-E-";
                    }
                    let colorString = "red";
                    let binId = b.binId;
                    // Normalize for comparison - API may use siteId/SiteId, binId/BinId; bins may use binId, binDataId, number, or name
                    const getApiSiteId = (x) => x.siteId ?? x.SiteId ?? x.siteGuid ?? x.SiteGuid;
                    const getApiBinId = (x) => x.binId ?? x.BinId;
                    const binIdsToMatch = [b.binId, b.binDataId, b.number, b.name].filter(v => v != null);
                    const matchBinId = (apiBinId) => {
                        if (apiBinId == null) return false;
                        const apiNorm = typeof apiBinId === 'number' ? apiBinId : parseInt(apiBinId, 10);
                        for (const bid of binIdsToMatch) {
                            const bNorm = typeof bid === 'number' ? bid : parseInt(bid, 10);
                            if (!isNaN(apiNorm) && !isNaN(bNorm) && apiNorm === bNorm) return true;
                            if (String(apiBinId) === String(bid)) return true;
                        }
                        return false;
                    };
                    const siteMatchesForBin = (x) => global.normalizeGuidForCompare(getApiSiteId(x)) === global.normalizeGuidForCompare(siteInfo.siteGuid);
                    let binWeatherStationReadings = (global.gw.currentWeatherReadings || []).filter(x =>
                        siteMatchesForBin(x) && matchBinId(getApiBinId(x))
                    );
                    let hours = 24;
                    // Get bin grade from API data, or null if not available
                    // Ensure binId is a number for Map lookup consistency
                    const lookupBinId = typeof binId === 'number' ? binId : parseInt(binId, 10);
                    const binGradeAndAverageTimeToRead = (!isNaN(lookupBinId) && binGrades.has(lookupBinId)) ? binGrades.get(lookupBinId) : null;
                    const binGrade = binGradeAndAverageTimeToRead ? binGradeAndAverageTimeToRead.grade : null;
                    const averageTimeToRead = binGradeAndAverageTimeToRead ? binGradeAndAverageTimeToRead.averageTimeToRead : null;
                    const gradeDateReadRaw = binGradeAndAverageTimeToRead ? binGradeAndAverageTimeToRead.dateRead : null;
                    const gradeDateRead = (() => {
                        if (gradeDateReadRaw == null) return null;
                        try {
                            const dateStr = typeof gradeDateReadRaw === 'string' ? gradeDateReadRaw.replace("Z", "-05:00") : gradeDateReadRaw;
                            const d = new Date(dateStr);
                            if (isNaN(d.getTime()) || d.getFullYear() < 2000) return null;
                            return d.toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "");
                        } catch {
                            return null;
                        }
                    })();
                    
                    // Check if we have weather station history data for this bin
                    const binWeatherStationData = !isNaN(lookupBinId) && binWeatherStationHistory.has(lookupBinId) 
                        ? binWeatherStationHistory.get(lookupBinId) 
                        : null;

                    const co2ReadDeviceIds = (() => {
                        const fromReadings = binWeatherStationReadings.map(bwr => bwr.DeviceId).filter(id => id != null && id !== "");
                        if (fromReadings.length > 0) return fromReadings;
                        if (!co2Type1DeviceIdsByBin) return [];
                        const candidates = [lookupBinId, b.binId, b.binDataId, b.number].filter(v => v != null);
                        for (const c of candidates) {
                            const k = typeof c === "number" ? c : parseInt(c, 10);
                            if (!isNaN(k) && co2Type1DeviceIdsByBin[k]) {
                                return co2Type1DeviceIdsByBin[k];
                            }
                            const str = String(c);
                            if (co2Type1DeviceIdsByBin[str]) {
                                return co2Type1DeviceIdsByBin[str];
                            }
                        }
                        return [];
                    })();

                    // Use normal bin temperature logic
                    if (b.averageTemperature !== 0) {
                        if (mainSelection === "co2") {
                            if (subSelection === "today") {
                                if (subCo2 && subCo2.length > 0 && subCo2[0] && subCo2[0].childNodes) {
                                    if (subCo2[0].childNodes[0]) {
                                        subCo2[0].childNodes[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                    }
                                    if (subCo2[0].childNodes[2]) {
                                        subCo2[0].childNodes[2].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                                    }
                                    if (subCo2[0].childNodes[4]) {
                                        subCo2[0].childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                    }
                                }
                                hours = 24;
                            }
                            else if (subSelection === "yesterday") {
                                if (subCo2 && subCo2.length > 0 && subCo2[0] && subCo2[0].childNodes) {
                                    if (subCo2[0].childNodes[0]) {
                                        subCo2[0].childNodes[0].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                    }
                                    if (subCo2[0].childNodes[2]) {
                                        subCo2[0].childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                    }
                                    if (subCo2[0].childNodes[4]) {
                                        subCo2[0].childNodes[4].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                                    }
                                }
                                hours = 48;
                            }
                            else {
                                if (binWeatherStationReadings && binWeatherStationReadings.length > 0) {
                                    let mappedNumbers = binWeatherStationReadings.map(x => +x.CarbonDioxide);
                                    let maxCarbonDioxide = Math.max.apply(Math, mappedNumbers);
                                    temperature = maxCarbonDioxide;
                                    for (var wr = 0; wr < binWeatherStationReadings.length; wr++) {
                                        let binWeatherStationReading = binWeatherStationReadings[wr];
                                        binWeatherStationReading.color = global.getCarbondioxideColor(maxCarbonDioxide, binWeatherStationReading.CriticalLimit, binWeatherStationReading.HighLimit, binWeatherStationReading.ModerateLimit, binWeatherStationReading.WarningLimit);
                                    }
                                }

                                if (subCo2 && subCo2.length > 0 && subCo2[0] && subCo2[0].childNodes) {
                                    if (subCo2[0].childNodes[0]) {
                                        subCo2[0].childNodes[0].style.color = getThemeColor("rgb(45, 45, 45)", "rgb(244, 204, 142)");
                                    }
                                    if (subCo2[0].childNodes[2]) {
                                        subCo2[0].childNodes[2].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                    }
                                    if (subCo2[0].childNodes[4]) {
                                        subCo2[0].childNodes[4].style.color = getThemeColor("rgb(45, 45, 45, 0.7)", "rgb(222, 175, 87, 0.5)");
                                    }
                                }
                            }
                        }
                        else if (mainSelection === "scanTemp") {
                            // Always use normal bin temperature logic - weather station data is displayed separately
                            // Safeguard: If averageTemperature matches weather station temperature, treat as no scan data
                            const weatherStationTemp = binWeatherStationData && !binWeatherStationData.isStale 
                                ? binWeatherStationData.temperature 
                                : null;
                            const isAvgTempFromWeatherStation = weatherStationTemp != null && 
                                b.averageTemperature != null && 
                                Math.abs(b.averageTemperature - weatherStationTemp) < 0.5; // Within 0.5 degrees
                            
                            if (subSelection === "average") {
                                // For average temperature, show the average of valid temperatures only
                                // If all temperatures are -E-, the stored procedure returns NULL, which gets converted to 222
                                // Don't use averageTemperature if it matches weather station data (indicates no scan data)
                                if (!isAvgTempFromWeatherStation && b.averageTemperature && b.averageTemperature >= -50 && b.averageTemperature <= 250 && b.averageTemperature !== 222) {
                                    temperature = Math.round(b.averageTemperature) + "\u00B0";
                                    colorString = global.getTemperatureColorString(b.averageTemperature);
                                }
                                else {
                                    // Show -E- when there are no valid temperatures to calculate average from
                                    temperature = "-E-";
                                    colorString = "red";
                                }
                            }
                            else if (subSelection === "low") {
                                // For low temperature, show the lowest valid temperature
                                // If all temperatures are -E-, the stored procedure returns NULL, which gets converted to 222
                                // Don't use lowTemperature if it matches weather station data (indicates no scan data)
                                const isLowTempFromWeatherStation = weatherStationTemp != null && 
                                    b.lowTemperature != null && 
                                    Math.abs(b.lowTemperature - weatherStationTemp) < 0.5;
                                if (!isLowTempFromWeatherStation && b.lowTemperature && b.lowTemperature >= -50 && b.lowTemperature <= 250 && b.lowTemperature !== 222) {
                                    temperature = Math.round(b.lowTemperature) + "\u00B0";
                                    colorString = global.getTemperatureColorString(b.lowTemperature);
                                }
                                else {
                                    // Show -E- when there are no valid temperatures to calculate low from
                                    temperature = "-E-";
                                    colorString = "red";
                                }
                            }
                            else {
                                // For high temperature, we want to show the highest valid temperature
                                // The stored procedure filters out -E- values and calculates max from valid temperatures only
                                // If all temperatures are -E-, the stored procedure returns NULL, which gets converted to 222
                                

                                

                                
                                // Don't use highTemperature if it matches weather station data (indicates no scan data)
                                const isHighTempFromWeatherStation = weatherStationTemp != null && 
                                    b.highTemperature != null && 
                                    Math.abs(b.highTemperature - weatherStationTemp) < 0.5;
                                if (!isHighTempFromWeatherStation && b.highTemperature && b.highTemperature >= -50 && b.highTemperature <= 250 && b.highTemperature !== 222) {
                                    temperature = Math.round(b.highTemperature) + "\u00B0";
                                    colorString = global.getTemperatureColorString(b.highTemperature);
                                }
                                else {
                                    // Show -E- when there are no valid temperatures to calculate high from
                                    temperature = "-E-";
                                    colorString = "red";
                                }
                            }
                        }
                    }

                    let dateFrom = new Date();
                    dateFrom.setHours(dateFrom.getHours() - hours);

                    setCurrent(!current);

                    const cardBinFillRatio = global.getCardBinLevelFillRatio(siteBinLevelRows, b);
                    const binLevelTooltipText = global.formatBinLevelTooltipTextFromRows(siteBinLevelRows, b);
                    const binLevelClipId = `sitecard-bl-${String(siteInfo.siteGuid).replace(/[^a-zA-Z0-9]/g, "-")}-${String(b.binDataId ?? b.binId ?? binId ?? i)}-${i}`;
                    const openInventoryBinId = (() => {
                        const raw = b.binDataId != null && b.binDataId !== "" ? b.binDataId : b.binId ?? b.number;
                        if (raw == null || raw === "") return null;
                        const n = typeof raw === "number" ? raw : parseInt(raw, 10);
                        return Number.isNaN(n) ? null : n;
                    })();
                    const levelRowForGrain = Array.isArray(siteBinLevelRows) ? global.findBinLevelRowForCardBin(siteBinLevelRows, b) : null;
                    const rawContentsForTile = levelRowForGrain
                        ? String(levelRowForGrain.CurrentContents ?? levelRowForGrain.currentContents ?? "").trim()
                        : "";
                    const tileGrainLabel =
                        rawContentsForTile && rawContentsForTile.toLowerCase() !== "empty"
                            ? global.formatBinLevelContentsDisplay(rawContentsForTile)
                            : "";
                    const isFlatHouse = global.isDashboardBinFlatHouse(
                        b,
                        siteInventoryBinDimensions,
                        siteInventoryBins,
                    );
                    return (
                        <MuiMaterials.Box
                            className={"binContainer"}
                            key={`binElement${binId}_${binWeatherStationData ? binWeatherStationData.temperature : 'none'}_${binWeatherStationHistoryUpdate}`}
                            siteid={siteInfo.siteGuid}
                            binid={binId}
                            sx={{
                                borderRadius: "0.75rem",
                                border: global.gw.theme === 'light'
                                    ? "1px solid rgba(0, 0, 0, 0.2)"
                                    : "1px solid rgba(244, 204, 142, 0.3)",
                                background: global.gw.theme === 'light' ? "#f8f8f8" : "#313131",
                                color: global.gw.theme === 'light' ? "#2d2d2d" : "var(--card-foreground, #fff)",
                                boxShadow: global.gw.theme === 'light'
                                    ? "0 8px 32px 0 rgba(0,0,0,0.1), 0 1.5px 8px 0 rgba(0,0,0,0.05)"
                                    : "0 8px 32px 0 rgba(0,0,0,0.25), 0 1.5px 8px 0 rgba(244,204,142,0.10)",
                                transform: "translateY(-8px)",
                                transition: "box-shadow 0.3s cubic-bezier(.4,2,.6,1), transform 0.3s cubic-bezier(.4,2,.6,1)",
                                padding: "0.5rem",
                                margin: "0.25rem",
                                position: "relative",
                                minWidth: "100px",
                                minHeight: "130px",
                                flex: "1 1 auto",
                                alignSelf: "stretch",
                                textAlign: "left",
                                display: (mainSelection === "co2" && (subSelection === "current" || subSelection === "today" || subSelection === "yesterday")) || mainSelection !== "co2" ? "block" : "none",
                            }}
                        >
                            <MuiMaterials.Box sx={{ marginTop: "-5px", width: "100%", position: "relative", zIndex: 2 }}>
                                <MuiMaterials.Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        flexWrap: "nowrap",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <MuiMaterials.Typography
                                        sx={{
                                            flex: "1 1 auto",
                                            minWidth: 0,
                                            color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                            fontWeight: 900,
                                            fontSize: 14,
                                            lineHeight: 1.15,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {b.name}
                                    </MuiMaterials.Typography>
                                    <MuiMaterials.Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", flexShrink: 0, gap: 0.25 }}>
                                        <MuiMaterials.Button
                                            sx={{
                                                maxWidth: "28px",
                                                maxHeight: "28px",
                                                minWidth: "28px",
                                                minHeight: "28px",
                                                fontSize: "20px",
                                                color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                                cursor: "pointer",
                                                display: (mainSelection === "co2" && (subSelection === "current" || subSelection === "today" || subSelection === "yesterday")) ? "flex" : "none",
                                                justifyContent: "flex-start",
                                                marginLeft: "2px",
                                            }}
                                            onClick={e => {
                                                co2(e, binId, b.name);
                                            }}
                                        >
                                            <TuneIcon />
                                        </MuiMaterials.Button>
                                        {!siteInfo.isHms && openInventoryBinId != null ? (
                                            <MuiMaterials.Tooltip
                                                title="View or edit inventory for this bin."
                                                arrow
                                                placement="top"
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: getThemeColor("#ffffff", "#2d2d2d"),
                                                            color: getThemeColor("#2d2d2d", "#f4cc8e"),
                                                            border: `1px solid ${getThemeColor("rgba(0, 0, 0, 0.12)", "rgba(244, 204, 142, 0.35)")}`,
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            boxShadow: getThemeColor(
                                                                "0 4px 16px rgba(0,0,0,0.12)",
                                                                "0 4px 16px rgba(0,0,0,0.45)"
                                                            ),
                                                        },
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: getThemeColor("#ffffff", "#2d2d2d"),
                                                        },
                                                    },
                                                }}
                                            >
                                                <MuiMaterials.Button
                                                    variant="text"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.dispatchEvent(
                                                            new CustomEvent("grainwave-open-site-inventory-bin", {
                                                                detail: { siteGuid: siteInfo.siteGuid, binId: openInventoryBinId },
                                                            })
                                                        );
                                                    }}
                                                    sx={{
                                                        fontSize: "8px",
                                                        fontWeight: 600,
                                                        lineHeight: 1.15,
                                                        minWidth: 0,
                                                        margin: 0,
                                                        padding: "2px 0 2px 6px",
                                                        textTransform: "none",
                                                        color: getThemeColor("#2d2d2d", "#f4cc8e"),
                                                        cursor: "pointer",
                                                        whiteSpace: "nowrap",
                                                        "&:hover": {
                                                            backgroundColor: getThemeColor(
                                                                "rgba(0, 0, 0, 0.06)",
                                                                "rgba(244, 204, 142, 0.12)"
                                                            ),
                                                        },
                                                    }}
                                                >
                                                    Inventory
                                                </MuiMaterials.Button>
                                            </MuiMaterials.Tooltip>
                                        ) : null}
                                    </MuiMaterials.Box>
                                </MuiMaterials.Box>
                                {tileGrainLabel ? (
                                    <MuiMaterials.Typography
                                        sx={{
                                            color: getThemeColor("rgba(45, 45, 45, 0.7)", "rgba(244, 204, 142, 0.6)"),
                                            fontSize: 11,
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            marginTop: "2px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {tileGrainLabel}
                                    </MuiMaterials.Typography>
                                ) : null}
                            </MuiMaterials.Box>
                            <MuiMaterials.Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    zIndex: 1,
                                    pointerEvents: "none",
                                    "& > *": { pointerEvents: "auto" },
                                }}
                            >
                                {(binWeatherStationReadings && binWeatherStationReadings.length > 0) && (subSelection === "today" || subSelection === "yesterday") ?
                                    <MuiMaterials.Box
                                        sx={{
                                            margin: "0 auto",
                                        }}
                                    >
                                        {subSelection === "today" ?
                                            <>  
                                                <CarbonDioxideChartTile
                                                    binid={binId}
                                                    deviceid={"All"}
                                                    siteid={siteInfo.siteGuid}
                                                    dateFrom={dateFrom}
                                                    dateTo={new Date()}
                                                    hourFrom={0}
                                                    hourTo={24}
                                                />

                                            </>
                                        :
                                            <CarbonDioxideChartTile
                                                binid={binId}
                                                deviceid={"All"}
                                                siteid={siteInfo.siteGuid}
                                                dateFrom={dateFrom}
                                                dateTo={new Date()}
                                                hourFrom={0}
                                                hourTo={24}
                                            />
                                        }
                                        
                                    </MuiMaterials.Box>
                                    :
                                    (mainSelection === "co2" || mainSelection !== "co2") ?
                                        <MuiMaterials.Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "100%",
                                            }}
                                        >
                                                {binGrade !== null ? (
                                                    <MuiMaterials.Tooltip
                                                        title={
                                                            <span style={{ display: "block", textAlign: "left", maxWidth: 320 }}>
                                                                <span>
                                                                    Grade: {Math.round(binGrade)}%{gradeDateRead != null ? ` • Grade date: ${gradeDateRead}` : ''}{averageTimeToRead != null && !isNaN(averageTimeToRead) ? ` • Avg time to read: ${averageTimeToRead}s` : ''}
                                                                </span>
                                                                {binLevelTooltipText ? (
                                                                    <>
                                                                        <br /><br />
                                                                        <span style={{ whiteSpace: "pre-line" }}>{binLevelTooltipText}</span>
                                                                    </>
                                                                ) : null}
                                                            </span>
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <MuiMaterials.Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={(e) => { handleBinClick(e, b.binId, b.name); }}
                                                        >
                                                            <CardBinGraphic
                                                                isFlatHouse={isFlatHouse}
                                                                fillRatio={cardBinFillRatio}
                                                                clipId={binLevelClipId}
                                                                binGrade={binGrade}
                                                                temperature={temperature}
                                                                colorString={colorString}
                                                                showGradeRing
                                                            />
                                                        </MuiMaterials.Box>
                                                    </MuiMaterials.Tooltip>
                                                ) : binLevelTooltipText ? (
                                                    <MuiMaterials.Tooltip
                                                        title={
                                                            <span style={{ display: "block", textAlign: "left", maxWidth: 320, whiteSpace: "pre-line" }}>
                                                                {binLevelTooltipText}
                                                            </span>
                                                        }
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <MuiMaterials.Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={(e) => { handleBinClick(e, b.binId, b.name); }}
                                                        >
                                                            <CardBinGraphic
                                                                isFlatHouse={isFlatHouse}
                                                                fillRatio={cardBinFillRatio}
                                                                clipId={binLevelClipId}
                                                                binGrade={binGrade}
                                                                temperature={temperature}
                                                                colorString={colorString}
                                                            />
                                                        </MuiMaterials.Box>
                                                    </MuiMaterials.Tooltip>
                                                ) : (
                                                    <CardBinGraphic
                                                        isFlatHouse={isFlatHouse}
                                                        fillRatio={cardBinFillRatio}
                                                        clipId={binLevelClipId}
                                                        binGrade={binGrade}
                                                        temperature={temperature}
                                                        colorString={colorString}
                                                        onClick={(e) => { handleBinClick(e, b.binId, b.name); }}
                                                    />
                                                )}
                                                {mainSelection === "co2" ?
                                                    <HoldTooltip
                                                        className="carbonDioxideRise"
                                                        sx={{
                                                            display: "none",
                                                            textAlign: "center",
                                                            position: "relative",
                                                            top: "-94px",
                                                            left: "39px",
                                                            //transform: "scale(1.5)",
                                                            //fontFamily: Assets.Fonts.MontserratExtraLightName,
                                                            //fontSize: "25px",
                                                            //fontWeight: 900,
                                                            color: `${colorString}`
                                                        }}
                                                        tooltipContent={
                                                            <div>Rate of Rise breached.</div>
                                                        }
                                                    >
                                                        <StraightIcon
                                                            className="blink"
                                                            sx={{
                                                                transform: "scale(1.5)",
                                                                //fontFamily: Assets.Fonts.MontserratExtraLightName,
                                                                //fontSize: "25px",
                                                                //fontWeight: 900,
                                                                //position: "relative",
                                                                //top: "0px",
                                                                //left: "27px",
                                                                color: `${colorString}`,
                                                                marginLeft: "30px",
                                                            }}
                                                        />
                                                    </HoldTooltip>
                                                    : null}
                                            </MuiMaterials.Box>
                                        : null
                                }
                            </MuiMaterials.Box>
                            {mainSelection === "co2" ? (
                                <MuiMaterials.Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        zIndex: 2,
                                        display: "inline-flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        width: "100%",
                                        paddingBottom: "4px",
                                    }}
                                >
                                    {binWeatherStationReadings.length > 0 ? binWeatherStationReadings.map(bwr => {
                                        return (
                                            <HoldTooltip
                                                key={`readingsspan${bwr.WeatherStationReadingId}`}
                                                name="readingsSpan"
                                                sx={{
                                                    display: "inline-flex",
                                                    borderStyle: "ridge",
                                                    borderWidth: "0.5px",
                                                    borderColor: "transparent",
                                                    margin: "1px",
                                                    alignItems: "center",
                                                }}
                                                tooltipContent={
                                                    <div style={{}}>
                                                        <div>
                                                            {`Device Id: ${bwr.DeviceId}`}
                                                        </div>
                                                        <div>
                                                            {`Last Reading:  ${global.jsonDateToFormatDate(bwr.DateRead)}`}
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                <MuiMaterials.Typography
                                                    sx={{
                                                        color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                                        fontWeight: 900,
                                                        fontSize: "11px",
                                                        marginRight: "5px",
                                                    }}
                                                >
                                                    CO{<sub>2</sub>}
                                                </MuiMaterials.Typography>
                                                <MuiMaterials.Typography
                                                    className={"dashboardBinCo2"}
                                                    deviceid={bwr.DeviceId}
                                                    sx={{
                                                        color: global.getCarbondioxideColor(bwr.CarbonDioxide, bwr.CriticalLimit, bwr.HighLimit, bwr.ModerateLimit, bwr.WarningLimit),
                                                        fontWeight: 900,
                                                        fontSize: "11px",
                                                        ...(global.gw.theme === 'light' ? {
                                                            background: "rgb(59, 62, 68)",
                                                            padding: "2px 4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid rgba(0, 0, 0, 0.1)",
                                                        } : {
                                                            background: "rgb(59, 62, 68)",
                                                            padding: "2px 4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid rgba(244, 204, 142, 0.2)",
                                                        }),
                                                    }}
                                                >
                                                    {bwr.CarbonDioxide}
                                                </MuiMaterials.Typography>
                                                <StraightIcon
                                                    className="blink carbonDioxideRise"
                                                    sx={{
                                                        display: "none",
                                                        textAlign: "center",
                                                        position: "relative",
                                                        fontSize: "25px",
                                                        fontWeight: 900,
                                                        color: `${colorString}`,
                                                        height: "0.55em",
                                                        marginLeft: "-5px",
                                                    }}
                                                />
                                            </HoldTooltip>
                                        );
                                    }) : (
                                        <MuiMaterials.Typography
                                            sx={{
                                                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                fontWeight: 500,
                                                fontSize: "11px",
                                            }}
                                        >
                                            CO{<sub>2</sub>} —
                                        </MuiMaterials.Typography>
                                    )}
                                    {co2ReadDeviceIds.length > 0 ? (
                                        <MuiMaterials.Button
                                            size="small"
                                            variant="outlined"
                                            disabled={readingCO2BinId === lookupBinId}
                                            onClick={(e) => handleOpenCO2ReadModal(lookupBinId, co2ReadDeviceIds, e)}
                                            sx={{
                                                fontSize: 11,
                                                marginLeft: "6px",
                                                minWidth: "120px",
                                                cursor: readingCO2BinId === lookupBinId ? 'wait' : 'pointer',
                                                alignItems: 'baseline',
                                                '& > *': { pointerEvents: 'none' },
                                                '& sub': { verticalAlign: 'sub', fontSize: '0.75em' },
                                                ...outlinedActionButtonSx(),
                                            }}
                                            aria-label="Get Current CO2"
                                        >
                                            {readingCO2BinId === lookupBinId ? (
                                                <Spinner size="tiny" />
                                            ) : (
                                                <>Get Current CO{<sub>2</sub>}</>
                                            )}
                                        </MuiMaterials.Button>
                                    ) : null}
                                </MuiMaterials.Box>
                            ) : null}
                            
                            {/* Weather Station Temperature and Humidity at bottom left */}
                            {binWeatherStationData && (
                                <MuiMaterials.Box
                                    sx={{
                                        position: "absolute",
                                        bottom: "0.5rem",
                                        left: "0.5rem",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: global.gw.theme === "light" ? "4px" : "2px",
                                        ...(global.gw.theme === "light"
                                            ? {
                                                  padding: "5px 7px",
                                                  borderRadius: "6px",
                                                  backgroundColor: "rgba(0, 0, 0, 0.165)",
                                                  border: "1px solid rgba(0, 0, 0, 0.22)",
                                                  boxShadow:
                                                      "inset 0 1px 0 rgba(255, 255, 255, 0.55), 0 1px 4px rgba(0, 0, 0, 0.16)",
                                                  maxWidth: "calc(100% - 12px)",
                                              }
                                            : {}),
                                    }}
                                >
                                    {(binWeatherStationData.temperature != null || binWeatherStationData.isStale) && (
                                        <MuiMaterials.Typography
                                            sx={{
                                                color: global.gw.theme === 'light' ? "#1a1a1a" : "#f4cc8e",
                                                fontWeight: 900,
                                                fontSize: 14,
                                                lineHeight: 1,
                                                marginRight: "15px",
                                            }}
                                        >
                                            AT: {binWeatherStationData.isStale || binWeatherStationData.temperature == null ? "N/A" : Math.round(binWeatherStationData.temperature) + "\u00B0"}
                                        </MuiMaterials.Typography>
                                    )}
                                    {(binWeatherStationData.humidity != null || binWeatherStationData.isStale) && (
                                        <MuiMaterials.Typography
                                            sx={{
                                                color: global.gw.theme === 'light' ? "#1a1a1a" : "#f4cc8e",
                                                fontWeight: 900,
                                                fontSize: 14,
                                                lineHeight: 1,
                                                marginRight: "15px",
                                            }}
                                        >
                                            AH: {binWeatherStationData.isStale || binWeatherStationData.humidity == null ? "N/A" : Math.round(binWeatherStationData.humidity) + "%"}
                                        </MuiMaterials.Typography>
                                    )}
                                </MuiMaterials.Box>
                            )}
                        </MuiMaterials.Box>
                    );
                })
            );
        }
    };

    return (
        <MuiMaterials.Box
            className={`siteCard${isCollapsed ? "" : " siteCard--expanded"}`}
            siteid={siteInfo.siteGuid}
            key={`sitecard_box{site.siteIndex}`}
            onTouchStart={handleCardTouchStart}
            onTouchEnd={handleCardTouchEnd}
            onTouchCancel={() => { touchStartY.current = null; touchStartScrollTop.current = null; }}
            onClick={(e) => {
                if (isInteractiveCardTarget(e)) return;
                // Skip if we already handled via touch (mobile) to avoid double-expand / ghost collapse
                if (consumeTouchHandledClick(e)) return;
                // Only expand, don't collapse when clicking on the card
                if (isCollapsed) {
                    if (onExpandedChange) {
                        onExpandedChange(true);
                    } else {
                        setInternalCollapsed(false);
                    }
                    checkAndStartCO2Timer();
                }
            }}
            sx={{
                minWidth: 0,
                width: "100%",
                height: "auto",
                boxSizing: "border-box",
                ...(isCollapsed
                    ? {
                        flexDirection: "column",
                        justifyContent: "center",
                        minHeight: collapsedCardMinHeight(),
                    }
                    : null),
                margin: 0,
                touchAction: "pan-y",
                "& *": { touchAction: "pan-y" },
                // Row chrome comes from `.site-cards-list` in CustomerSites (shared Bin Inventory look).
                background: "transparent",
                borderRadius: 0,
                border: "none",
                boxShadow: "none",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
                color: global.gw.theme === 'light' ? "#000000" : "#ffffff",
                transition: "background-color 0.15s ease",
                position: "relative",
                overflow: isCollapsed ? "hidden" : "visible",
                cursor: isCollapsed ? "pointer" : "default",
                "&:hover": {
                    transform: "none",
                },
                "&::before": { display: "none" },
                display: global.gw.siteFilter === 0 || global.gw.siteFilter === siteInfo.siteGuid
                    ? (isCollapsed ? "flex" : "block")
                    : "none",
            }}>
                            <MuiMaterials.Card
                    sx={{
                        bgcolor: "transparent",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexWrap: "nowrap",
                        gap: { xs: 1, sm: 1.25 },
                        boxShadow: "none",
                        borderRadius: 0,
                        padding: { xs: "10px 12px", sm: "12px 14px" },
                        position: "relative",
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                <MuiMaterials.Box
                    data-site-card-header-toggle
                    onClick={handleHeaderAreaClick}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        gap: { xs: 1, sm: 1.25 },
                        flex: 1,
                        minWidth: 0,
                        cursor: isCollapsed ? "inherit" : "pointer",
                    }}
                >
                        <MuiMaterials.Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                flex: 1,
                                minWidth: 0,
                                gap: "2px",
                            }}
                        >
                        <MuiMaterials.Typography
                            key={`sitecard_typo${siteInfo.siteIndex}`}
                            sx={{
                                fontSize: { xs: 12, sm: 13, md: 14 },
                                fontWeight: 700,
                                margin: 0,
                                marginBottom: "4px",
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                letterSpacing: "0.5px",
                                lineHeight: 1.2,
                                color: global.gw.theme === "light" ? "#2d2d2d" : "#f4cc8e",
                                textShadow: global.gw.theme === "light"
                                    ? "0 1px 2px rgba(0, 0, 0, 0.1)"
                                    : "0 1px 2px rgba(0, 0, 0, 0.5)",
                                ...(global.gw.theme === "light"
                                    ? {
                                          color: "#2d2d2d",
                                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                                      }
                                    : {
                                          background: "linear-gradient(135deg, #ffe2aa 0%, #fff1c9 100%)",
                                          backgroundClip: "text",
                                          WebkitBackgroundClip: "text",
                                          WebkitTextFillColor: "transparent",
                                          filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                                      }),
                            }}
                        >
                            {siteInfo.siteName}
                        </MuiMaterials.Typography>
                        <MuiMaterials.Box
                            component="div"
                            sx={{
                                display: "inline-flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                alignItems: "center",
                                margin: 0,
                                padding: "0 2px",
                                width: "100%",
                                gap: 0,
                                columnGap: 0,
                                fontSize: { xs: 11.5, sm: 12.5 },
                                fontWeight: 600,
                                lineHeight: 1,
                                letterSpacing: "0.25px",
                            }}
                        >
                            <MuiMaterials.Box
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1,
                                    ...(global.gw.theme === "light"
                                        ? {
                                              padding: "3px 7px",
                                              borderRadius: "6px",
                                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                                              border: "1px solid rgba(0, 0, 0, 0.22)",
                                              boxSizing: "border-box",
                                              boxShadow: "none",
                                              marginBottom: "3px",
                                          }
                                        : {}),
                                    color: global.getTemperatureColorString(
                                        weatherStationHistory?.temperature != null
                                            ? weatherStationHistory.temperature
                                            : siteInfo.currentTemperature,
                                    ),
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                <MuiMaterials.Box
                                    component="span"
                                    sx={{
                                        display: "inline-block",
                                        lineHeight: 1,
                                        ...(global.gw.theme === "light"
                                            ? {
                                                  transform: "translateY(1px)",
                                              }
                                            : {}),
                                    }}
                                >
                                    {weatherStationHistory?.temperature != null
                                        ? `${weatherStationHistory.temperature}\u00B0`
                                        : siteInfo.currentTemperature != null
                                            ? `${siteInfo.currentTemperature}\u00B0`
                                            : "—"}
                                </MuiMaterials.Box>
                            </MuiMaterials.Box>
                            <MuiMaterials.Box
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1,
                                    color: global.gw.theme === "light" ? "rgba(0,0,0,0.42)" : "rgba(244,204,142,0.45)",
                                    mx: "8px",
                                    fontWeight: 500,
                                    userSelect: "none",
                                }}
                            >
                                |
                            </MuiMaterials.Box>
                            <MuiMaterials.Box
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1,
                                    color:
                                        global.gw.theme === "light"
                                            ? "#363636"
                                            : "#e8dcc4",
                                    fontVariantNumeric: "tabular-nums",
                                    fontWeight: 600,
                                }}
                            >
                                {weatherStationHistory?.humidity != null
                                    ? `${weatherStationHistory.humidity}%`
                                    : siteInfo.currentHumidity != null
                                        ? `${siteInfo.currentHumidity}%`
                                        : "—"}
                            </MuiMaterials.Box>
                        </MuiMaterials.Box>
                            <MuiMaterials.Tooltip
                                title={isBlinking ? lastScanAlertTitle : ""}
                                arrow
                                placement="top"
                                disableHoverListener={!isBlinking}
                                disableFocusListener={!isBlinking}
                                disableTouchListener={!isBlinking}
                            >
                            <MuiMaterials.Typography
                                key={`sitecard_typo_scan${siteInfo.siteIndex}`}
                                className={isBlinking ? "last-scan-alert" : ""}
                                sx={{
                                    display: "block",
                                    margin: 0,
                                    width: "100%",
                                    color: isBlinking ? (global.gw.theme === 'light' ? "#d32f2f" : "#ff6b6b") : (global.gw.theme === 'light' ? "#2d2d2d" : "#a8abb1"),
                                    fontSize: { xs: 10, sm: 11, md: 12 },
                                    fontWeight: 500,
                                    opacity: 0.9,
                                    letterSpacing: "0.3px",
                                    textTransform: "uppercase",
                                    fontFamily: "monospace",
                                    ...(isBlinking ? (global.gw.theme === 'light' ? {
                                        background: "none",
                                        backgroundClip: "none",
                                        WebkitBackgroundClip: "none",
                                        WebkitTextFillColor: "inherit",
                                        filter: "none",
                                        fontWeight: 700,
                                        textShadow: "0 0 8px rgba(211, 47, 47, 0.6)",
                                    } : {
                                        background: "linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)",
                                        backgroundClip: "text",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        filter: "drop-shadow(0 1px 2px rgba(255, 107, 107, 0.3))",
                                    }) : {
                                        background: "none",
                                        backgroundClip: "none",
                                        WebkitBackgroundClip: "none",
                                        WebkitTextFillColor: "inherit",
                                        filter: "none",
                                    }),
                                }}
                            >
                                {isCcr ? "CCR" : `${isBlinking ? "⚠ " : "📅 "}${siteInfo.lastScan}`}
                            </MuiMaterials.Typography>
                            </MuiMaterials.Tooltip>
                {preloadedSiteIssue && (
                    <MuiMaterials.Typography
                        key={`sitecard_issue${siteInfo.siteIndex}`}
                        title={preloadedSiteIssue}
                        sx={{
                            display: "block",
                            margin: 0,
                            width: "100%",
                            color: global.gw.theme === 'light' ? "#d32f2f" : "#ff6b6b",
                            fontSize: { xs: 10, sm: 11, md: 12 },
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            opacity: 0.95,
                            letterSpacing: "0.3px",
                        }}
                    >
                        ⚠ {preloadedSiteIssue}
                    </MuiMaterials.Typography>
                )}
                        </MuiMaterials.Box>
                {!siteInfo.isHms && !isCcr && (() => {
                    // Use preloaded site grade from GetSiteGrade API if available, else fall back to average of bin grades
                    let displayGrade = null;
                    if (typeof preloadedSiteGrade === 'number' && !isNaN(preloadedSiteGrade) && preloadedSiteGrade >= 0 && preloadedSiteGrade <= 100) {
                        displayGrade = preloadedSiteGrade;
                    } else if (binGrades.size > 0) {
                        const gradeValues = Array.from(binGrades.values()).map(x => x.grade).filter(g => g != null && !isNaN(g));
                        displayGrade = gradeValues.length > 0 ? gradeValues.reduce((a, b) => a + b, 0) / gradeValues.length : null;
                    }
                    const gradeColor = global.getGradeColorString(displayGrade, global.gw.theme);
                    return displayGrade != null ? (
                        <MuiMaterials.Typography
                            sx={{
                                fontSize: { xs: 14, sm: 16, md: 18 },
                                fontWeight: 700,
                                marginRight: { xs: "6px", sm: "8px", md: "12px" },
                                flexShrink: 0,
                                letterSpacing: "0.5px",
                                lineHeight: 1.2,
                                textShadow: global.gw.theme === 'light' 
                                    ? "0 1px 2px rgba(0, 0, 0, 0.1)" 
                                    : "0 1px 2px rgba(0, 0, 0, 0.5)",
                                color: gradeColor || (global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e"),
                                ...(global.gw.theme === 'light' ? {
                                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                                } : {
                                    filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                                }),
                            }}
                        >
                            GQCS {Math.round(displayGrade)}%
                        </MuiMaterials.Typography>
                    ) : null;
                })()}
                </MuiMaterials.Box>
                {!siteInfo.isHms && !isCcr && (
                    <>
                        <MuiMaterials.IconButton
                            data-site-card-header-action
                            onClick={handleSiteNavMenuOpen}
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                            aria-controls={siteNavMenuAnchor ? `site-nav-menu-${siteInfo.siteGuid}` : undefined}
                            aria-haspopup="true"
                            aria-expanded={siteNavMenuAnchor ? "true" : undefined}
                            aria-label="Site navigation menu"
                            sx={siteCardHeaderActionButtonSx}
                        >
                            <MenuIcon sx={{ fontSize: { xs: '18px', md: '20px' } }} />
                        </MuiMaterials.IconButton>
                        <MuiMaterials.Menu
                            id={`site-nav-menu-${siteInfo.siteGuid}`}
                            anchorEl={siteNavMenuAnchor}
                            open={Boolean(siteNavMenuAnchor)}
                            onClose={handleSiteNavMenuClose}
                            onClick={(e) => e.stopPropagation()}
                            onWheel={(e) => e.stopPropagation()}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            {...withScrollHintMenuProps({
                                PaperProps: {
                                    sx: {
                                        mt: 0.5,
                                        minWidth: 180,
                                        maxWidth: "min(90vw, 480px)",
                                        background:
                                            global.gw.theme === "light" ? "#f8f8f8" : "#313131",
                                        border:
                                            global.gw.theme === "light"
                                                ? "1px solid rgba(0, 0, 0, 0.15)"
                                                : "1px solid rgba(244, 204, 142, 0.3)",
                                    },
                                },
                            })}
                        >
                            {getSiteCardMenuItems()
                                .filter((item) => {
                                    if (
                                        siteInfo?.isHms
                                        && (item.id === SITE_WINDOW_IDS.setup
                                            || item.id === SITE_WINDOW_IDS.versioning
                                            || item.id === SITE_WINDOW_IDS.bin)
                                    ) {
                                        return false;
                                    }
                                    return true;
                                })
                                .map((item) => (
                                    <MuiMaterials.MenuItem
                                        key={item.id}
                                        onClick={runSiteNavAction((e) => {
                                            if (item.id === SITE_WINDOW_IDS.sites3d) {
                                                return site3d(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.bin) {
                                                return bin3d(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.co2) {
                                                return co2(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.cableHealth) {
                                                return cableHealth(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.comparison) {
                                                return temperatureComparison(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.history) {
                                                return temperatureHistory(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.scan) {
                                                return scan(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.devices) {
                                                return devices(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.inventory) {
                                                return inventory(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.issues) {
                                                return siteIssues(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.layout) {
                                                return siteLayout(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.setup) {
                                                return siteSetup(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.versioning) {
                                                return siteVersioning(e);
                                            }
                                            if (item.id === SITE_WINDOW_IDS.grades) {
                                                saveScrollPosition();
                                                siteSelected(e);
                                                openSiteWindow(SITE_WINDOW_IDS.grades, siteInfo.siteGuid);
                                                return;
                                            }
                                            saveScrollPosition();
                                            siteSelected(e);
                                            openSiteWindow(item.id, siteInfo.siteGuid);
                                        })}
                                        sx={siteCardNavMenuItemSx}
                                    >
                                        {item.id === SITE_WINDOW_IDS.co2 ? <>CO<sub>2</sub></> : item.label}
                                    </MuiMaterials.MenuItem>
                                ))}
                        </MuiMaterials.Menu>
                    </>
                )}
            </MuiMaterials.Card>

            {!isCollapsed && isCcr && (
                <>
                    <MuiMaterials.Divider variant="middle" sx={{
                        borderColor: global.gw.theme === "light" ? "rgba(0, 0, 0, 0.2)" : "#ffffff1c",
                    }} />
                    <CcrPartList
                        siteGuid={siteInfo.siteGuid}
                        onOpen={(tag) => openCcr({ preventDefault() {}, stopPropagation() {} }, tag)}
                    />
                </>
            )}

            {/* HMS Data Section - Only for HMS sites */}
            {!isCollapsed && siteInfo.isHms && (
                <MuiMaterials.Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        alignContent: "center",
                        justifyContent: "center",
                        margin: "15px auto",
                        alignItems: "center",
                        position: "relative",
                        padding: "0px 15px",
                    }}
                >
                    {/* Multiple Devices Container */}
                    <MuiMaterials.Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            gap: "12px",
                        }}
                    >
                        {hmsLoading && (!hmsData || hmsData.length === 0) ? (
                            <MuiMaterials.Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    padding: "20px",
                                    color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                }}
                            >
                                Loading devices...
                            </MuiMaterials.Box>
                        ) : (
                            hmsData && hmsData.map((device, deviceIndex) => {
                                const showMa = device.ma !== undefined && device.ma !== null;
                                const columns = showMa ? 7 : 6; // 7 columns if mA is shown (including Reset), 6 if not
                                
                                return (
                                <MuiMaterials.Box
                                    key={`hms-device-${deviceIndex}`}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: showMa ? "1fr 1fr" : "1fr 1fr",
                                            sm: showMa ? "1fr 1fr 1fr" : "1fr 1fr 1fr",
                                            md: showMa ? "1fr 1fr 1fr 1fr 1fr 1fr 1fr" : "1fr 1fr 1fr 1fr 1fr 1fr"
                                        },
                                        gap: { xs: "8px", sm: "12px", md: "16px" },
                                        background: global.gw.theme === 'light'
                                            ? "#f8f8f8"
                                            : "#313131",
                                        borderRadius: "16px",
                                        padding: { xs: "12px", sm: "16px", md: "20px" },
                                        border: global.gw.theme === 'light'
                                            ? "1px solid rgba(0, 0, 0, 0.2)"
                                            : "1px solid rgba(244, 204, 142, 0.3)",
                                        boxShadow: global.gw.theme === 'light'
                                            ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                                            : "none",
                                        backdropFilter: global.gw.theme === 'light' ? "blur(20px)" : "none",
                                        WebkitBackdropFilter: global.gw.theme === 'light' ? "blur(20px)" : "none",
                                        position: "relative",
                                        overflow: "hidden",
                                        minHeight: "80px",
                                    }}
                                >
                                    {/* Device Name */}
                                    <MuiMaterials.Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: "600",
                                                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Device
                                        </MuiMaterials.Typography>
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "16px",
                                                fontWeight: "700",
                                                color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                                textAlign: "center",
                                                minWidth: "150px",
                                                maxWidth: "200px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {device.devicename || 'N/A'}
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.Box>

                                    {/* Device Status Indicator */}
                                    <MuiMaterials.Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: "600",
                                                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Status
                                        </MuiMaterials.Typography>
                                        <MuiMaterials.Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "4px 12px",
                                                borderRadius: "20px",
                                                backgroundColor: (device.rpm > 0 && device.motionDetected) ? 
                                                    (global.gw.theme === 'light' ? "#4caf50" : "#66bb6a") :
                                                    (global.gw.theme === 'light' ? "#f44336" : "#ef5350"),
                                                color: "white",
                                                fontSize: "12px",
                                                fontWeight: "700",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                boxShadow: global.gw.theme === 'light' 
                                                    ? "0 2px 4px rgba(0, 0, 0, 0.2)" 
                                                    : "0 2px 4px rgba(0, 0, 0, 0.4)",
                                            }}
                                        >
                                            {device.rpm > 0 && device.motionDetected ? 'ON' : 'OFF'}
                                        </MuiMaterials.Box>
                                    </MuiMaterials.Box>

                                    {/* RPM Data */}
                                    <MuiMaterials.Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: "600",
                                                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            RPM
                                        </MuiMaterials.Typography>
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "20px",
                                                fontWeight: "800",
                                                color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                                textShadow: global.gw.theme === 'light' ? "0 2px 4px rgba(0, 0, 0, 0.2)" : "0 2px 4px rgba(0, 0, 0, 0.4)",
                                                letterSpacing: "0.5px",
                                                lineHeight: 1,
                                                textAlign: "center",
                                                background: global.gw.theme === 'light'
                                                    ? "linear-gradient(135deg, #2d2d2d 0%, #404040 100%)"
                                                    : "linear-gradient(135deg, #f4cc8e 0%, #ffe2aa 100%)",
                                                backgroundClip: "text",
                                                WebkitBackgroundClip: "text",
                                                WebkitTextFillColor: "transparent",
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                border: global.gw.theme === 'light'
                                                    ? "1px solid rgba(0, 0, 0, 0.1)"
                                                    : "1px solid rgba(244, 204, 142, 0.2)",
                                                boxShadow: global.gw.theme === 'light'
                                                    ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                                                    : "0 2px 8px rgba(0, 0, 0, 0.3)",
                                                minWidth: "60px",
                                            }}
                                        >
                                            {device.rpm !== undefined && device.rpm !== null ? device.rpm : 'N/A'}
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.Box>

                                    {/* Milliamps (mA) - Only show if value exists and is not null */}
                                    {device.ma !== undefined && device.ma !== null && (
                                        <MuiMaterials.Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                            }}
                                        >
                                            <MuiMaterials.Typography
                                                sx={{
                                                    fontSize: "10px",
                                                    fontWeight: "600",
                                                    color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    marginBottom: "4px",
                                                }}
                                            >
                                                mA
                                            </MuiMaterials.Typography>
                                            <MuiMaterials.Typography
                                                sx={{
                                                    fontSize: "18px",
                                                    fontWeight: "700",
                                                    color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                                    textAlign: "center",
                                                    minWidth: "50px",
                                                }}
                                            >
                                                {device.ma}
                                            </MuiMaterials.Typography>
                                        </MuiMaterials.Box>
                                    )}

                                    {/* Battery Level */}
                                    <MuiMaterials.Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: "600",
                                                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Battery
                                        </MuiMaterials.Typography>
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: "700",
                                                color: device.batterylevel > 20 ? 
                                                    (global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e") :
                                                    (global.gw.theme === 'light' ? "#d32f2f" : "#ff6b6b"),
                                                textAlign: "center",
                                                minWidth: "60px",
                                            }}
                                        >
                                            {device.batterylevel !== undefined && device.batterylevel !== null ? device.batterylevel : 'N/A'}%
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.Box>

                                    {/* Last Reading */}
                                    <MuiMaterials.Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: "600",
                                                color: global.gw.theme === 'light' ? "rgba(45, 45, 45, 0.6)" : "rgba(244, 204, 142, 0.6)",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Last Reading
                                        </MuiMaterials.Typography>
                                        <MuiMaterials.Typography
                                            sx={{
                                                fontSize: { xs: "10px", sm: "12px", md: "14px" },
                                                fontWeight: "600",
                                                color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                                                textAlign: "center",
                                                wordBreak: "break-word",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {device.createdat ? (() => {
                                                const date = new Date(device.createdat);
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const year = date.getFullYear();
                                                const hours = date.getHours();
                                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                                const seconds = String(date.getSeconds()).padStart(2, '0');
                                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                                const displayHours = hours % 12 || 12;
                                                return `${month}/${day}/${year} ${displayHours}:${minutes}:${seconds} ${ampm}`;
                                            })() : 'N/A'}
                                        </MuiMaterials.Typography>
                                    </MuiMaterials.Box>

                                    {/* Reset Button */}
                                    <MuiMaterials.Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <MuiMaterials.Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleOpenResetModal(device.devicename)}
                                            sx={outlinedActionButtonSx()}
                                        >
                                            Reset
                                        </MuiMaterials.Button>
                                    </MuiMaterials.Box>
                                </MuiMaterials.Box>
                                );
                            })
                        )}
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            )}

            {/* Reset Device Confirmation Modal */}
            <MuiMaterials.Modal
                key={`reset-modal-${global.gw.theme}-${forceUpdate}`}
                open={resetModalOpen}
                onClose={handleCloseResetModal}
                aria-labelledby="reset-modal-title"
                aria-describedby="reset-modal-description"
                sx={{
                    backgroundColor: global.gw.theme === 'light' 
                        ? "rgba(255, 255, 255, 0.45)" 
                        : "rgba(0, 0, 0, 0.45)",
                }}
            >
                <MuiMaterials.Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: global.gw.theme === 'light' ? '#ffffff' : '#2d2d2d',
                    border: `1px solid ${global.gw.theme === 'light' ? '#cccccc' : 'rgba(244, 204, 142, 0.3)'}`,
                    boxShadow: 24,
                    p: 4,
                }}>
                    <MuiMaterials.Typography
                        id="reset-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{
                            color: global.gw.theme === 'light' ? '#333333' : '#f4cc8e',
                        }}
                    >
                        Reset Device?
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography
                        id="reset-modal-description"
                        sx={{
                            color: global.gw.theme === 'light' ? '#666666' : '#f4cc8e',
                            mt: 2,
                            mb: 2,
                        }}
                    >
                        This will reset the device if it is online and will take approximately 5 minutes to come back online. The counter will be set to 0.
                    </MuiMaterials.Typography>
                    <div style={confirmDialogActionsSx()}>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={confirmDialogButtonSx()}
                            onClick={handleCloseResetModal}
                        >
                            No
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={confirmDialogButtonSx()}
                            onClick={handleResetDevice}
                        >
                            Yes
                        </MuiMaterials.Button>
                    </div>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>

            {/* Get Current CO2 Confirmation Modal */}
            <MuiMaterials.Modal
                key={`co2-read-modal-${global.gw.theme}-${forceUpdate}`}
                open={co2ReadModalOpen}
                onClose={handleCloseCO2ReadModal}
                aria-labelledby="co2-read-modal-title"
                aria-describedby="co2-read-modal-description"
                sx={{
                    backgroundColor: global.gw.theme === 'light'
                        ? "rgba(255, 255, 255, 0.45)"
                        : "rgba(0, 0, 0, 0.45)",
                }}
            >
                <MuiMaterials.Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: global.gw.theme === 'light' ? '#ffffff' : '#2d2d2d',
                    border: `1px solid ${global.gw.theme === 'light' ? '#cccccc' : 'rgba(244, 204, 142, 0.3)'}`,
                    boxShadow: 24,
                    p: 4,
                }}>
                    <MuiMaterials.Typography
                        id="co2-read-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{
                            color: global.gw.theme === 'light' ? '#333333' : '#f4cc8e',
                        }}
                    >
                        Get Current CO{<sub>2</sub>}?
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography
                        id="co2-read-modal-description"
                        sx={{
                            color: global.gw.theme === 'light' ? '#666666' : '#f4cc8e',
                            mt: 2,
                            mb: 2,
                        }}
                    >
                        This will take approximately 1 minute to read the CO{<sub>2</sub>} sensors.
                    </MuiMaterials.Typography>
                    <div style={confirmDialogActionsSx()}>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={confirmDialogButtonSx()}
                            onClick={handleCloseCO2ReadModal}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={confirmDialogButtonSx()}
                            onClick={handleConfirmCO2Read}
                        >
                            OK
                        </MuiMaterials.Button>
                    </div>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>

            {!isCollapsed && !siteInfo.isHms && !isCcr && (
                <MuiMaterials.Divider variant="middle" sx={{
                    //margin: "10px", 
                    borderColor: global.gw.theme === 'light' ? "rgba(0, 0, 0, 0.2)" : "#ffffff1c",
                }} />
            )}
            {!siteInfo.isHms && !isCcr && (
                <Fragment>
                <MuiMaterials.Box
                key={`sitecard_toggle_${siteInfo.siteIndex}`}
                sx={{
                    //fontFamily: Assets.Fonts.MontserratExtraLightName,
                    //color: "#a8abb1",
                    fontSize: 14,
                    fontWeight: 1000,
                    margin: "10px 0px 0px 17px",
                    display: isCollapsed ? "none" : "flex",
                }}
            >
                <MuiMaterials.Tooltip title="Temperature readings at time of scan." arrow>
                    <MuiMaterials.Typography
                        className="showScanTemperatures"
                        siteid={siteInfo.siteGuid}
                        sx={{
                            color: global.gw.theme === 'light' ? "rgb(45, 45, 45, 0.7)" : "rgb(222, 175, 87, 0.5)",
                            cursor: "pointer",
                            fontWeight: 900,
                            fontSize: "12px",
                        }}
                        onClick={e => { renderBins("scanTemp", "high") }}
                    >
                        Temperature
                    </MuiMaterials.Typography>
                </MuiMaterials.Tooltip>
                <MuiMaterials.Box
                    orientation="vertical"
                    //variant="middle"
                    sx={{
                        margin: "-4px 10px",
                        color: global.gw.theme === 'light' ? "rgba(0, 0, 0, 0.3)" : "#ffffff1c",
                    }}
                >
                    {"|"}
                </MuiMaterials.Box>
                <MuiMaterials.Tooltip title="CO2 readings." arrow>
                    <MuiMaterials.Typography
                        className="showCarbonDioxide"
                        siteid={siteInfo.siteGuid}
                        sx={{
                            color: global.gw.theme === 'light' ? "rgb(45, 45, 45, 0.7)" : "rgb(222, 175, 87, 0.5)",
                        cursor: "pointer",
                            fontWeight: 900,
                            fontSize: "12px",
                            }}
                            onClick={e => { renderBins("co2", "current") }}
                        >
                        CO{<sub>2</sub>}
                    </MuiMaterials.Typography>
                </MuiMaterials.Tooltip>
            </MuiMaterials.Box>
            <MuiMaterials.Box
                key={`sitecard_c02toggle_${siteInfo.siteIndex}`}
                className="subCo2"
                sx={{
                    //fontFamily: Assets.Fonts.MontserratExtraLightName,
                    //color: "#a8abb1",
                    fontSize: 14,
                    fontWeight: 1000,
                    margin: "10px 17px",
                    display: isCollapsed ? "none" : (currentMainSelection === "co2" ? "flex" : "none"),
                }}
            >
                    <HoldTooltip
                        className="currentCo2"
                        sx={{
                            color: global.gw.theme === 'light' ? "rgb(45, 45, 45)" : "rgb(222, 175, 87)",
                            cursor: "pointer",
                            fontWeight: 900,
                            fontSize: "12px",
                        }}
                        onClick={(e) => {
                            renderBins("co2", "current");
                        }}
                        tooltipContent={<>Current C0{<sub>2</sub>} readings.</>} 
                    >
                        {"Current"}
                    </HoldTooltip>
                    <MuiMaterials.Box
                        orientation="vertical"
                        //variant="middle"
                        sx={{
                            margin: "-4px 10px",
                            color: global.gw.theme === 'light' ? "rgba(0, 0, 0, 0.3)" : "#ffffff1c",
                        }}
                    >
                        {"|"}
                    </MuiMaterials.Box>
                    <HoldTooltip
                        className="todayCo2"
                        sx={{
                            color: global.gw.theme === 'light' ? "rgb(45, 45, 45, 0.7)" : "rgb(222, 175, 87, 0.5)",
                            cursor: "pointer",
                            fontWeight: 900,
                            fontSize: "12px",
                        }}
                        onClick={e => { renderBins("co2", "today") }}
                        tooltipContent={<>CO<sub>2</sub> readings from 24 hours ago until now.</>} 
                    >
                        {"24h"}
                    </HoldTooltip>
                    <MuiMaterials.Box
                        orientation="vertical"
                        //variant="middle"
                        sx={{
                            margin: "-4px 10px",
                            color: global.gw.theme === 'light' ? "rgba(0, 0, 0, 0.3)" : "#ffffff1c",
                        }}
                    >
                        {"|"}
                    </MuiMaterials.Box>
                    <HoldTooltip
                        className="yesterdayCo2"
                        sx={{
                            color: global.gw.theme === 'light' ? "rgb(45, 45, 45, 0.7)" : "rgb(222, 175, 87, 0.5)",
                            cursor: "pointer",
                            fontWeight: 900,
                            fontSize: "12px",
                        }}
                        onClick={e => { renderBins("co2", "yesterday") }}
                        tooltipContent={<>All C0{<sub>2</sub>} readings from 48 hours ago until now.</>} 
                    >
                        {"48h"}
                    </HoldTooltip>
                </MuiMaterials.Box>
            <MuiMaterials.Box
                key={`sitecard_scanTemperatureToggle_${siteInfo.siteIndex}`}
                className="scanTemp"
                sx={{
                    fontSize: 14,
                    fontWeight: 1000,
                    margin: "10px 17px",
                    display: isCollapsed ? "none" : (currentMainSelection === "scanTemp" ? "flex" : "none"),
                }}
            >
                    <MuiMaterials.Tooltip title="Highest bin temperature at time of scan." arrow>
                        <MuiMaterials.Typography
                            className="showHighBinTemperatures"
                            sx={{
                                color: global.gw.theme === 'light' ? "rgb(45, 45, 45)" : "rgb(244, 204, 142)",
                                cursor: "pointer",
                                fontWeight: 900,
                                fontSize: "12px",
                            }}
                            onClick={e => { renderBins("scanTemp", "high") }}
                        >
                            {"High"}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Box
                        orientation="vertical"
                        sx={{
                            margin: "-4px 10px",
                            color: global.gw.theme === 'light' ? "rgba(0, 0, 0, 0.3)" : "#ffffff1c",
                        }}
                    >
                        {"|"}
                    </MuiMaterials.Box>
                    <MuiMaterials.Tooltip title="Average bin temperature at time of scan." arrow>
                        <MuiMaterials.Typography
                            className="showAverageBinTemperatures"
                            sx={{
                                color: global.gw.theme === 'light' ? "rgb(45, 45, 45, 0.7)" : "rgb(222, 175, 87, 0.5)",
                                cursor: "pointer",
                                fontWeight: 900,
                                fontSize: "12px",
                            }}
                            onClick={e => { renderBins("scanTemp", "average") }}
                        >
                            {"Average"}
                        </MuiMaterials.Typography>
                    </MuiMaterials.Tooltip>
                    <MuiMaterials.Box
                        orientation="vertical"
                        //variant="middle"
                        sx={{
                            margin: "-4px 10px",
                            color: global.gw.theme === 'light' ? "rgba(0, 0, 0, 0.3)" : "#ffffff1c",
                        }}
                    >
                        {"|"}
                    </MuiMaterials.Box>
                    <MuiMaterials.Tooltip title="Lowest bin temperature at time of scan." arrow>    
                        <MuiMaterials.Typography
                            className="showLowBinTemperatures"
                            sx={{
                                color: global.gw.theme === 'light' ? "rgb(45, 45, 45, 0.7)" : "rgb(222, 175, 87, 0.5)",
                                cursor: "pointer",
                                fontWeight: 900,
                                fontSize: "12px",
                            }}
                            onClick={e => { renderBins("scanTemp", "low") }}
                        >
                            {"Low"}
                        </MuiMaterials.Typography>
                                            </MuiMaterials.Tooltip>
                </MuiMaterials.Box>
                </Fragment>
            )}
            {!isCollapsed && !siteInfo.isHms && !isCcr && (
                <MuiMaterials.Box
                    className="binsContainer"
                    sx={{ 
                        //marginTop: "15px",
                        textAlign: "center",
                        display: "inline-flex",
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <MuiMaterials.Grid
                        sx={{
                            color: global.gw.theme === 'light' ? "#2d2d2d" : "#f4cc8e",
                            fontSize: "12px",
                            padding: "10px",
                            display: "flex",
                            justifyContent: "space-evenly",
                            flexWrap: "wrap",
                            alignItems: "stretch",
                            alignContent: "stretch",
                            gap: {
                                xs: "0px",  // No gap on small screens
                                sm: "0px",  // No gap on tablets
                                md: "0px"   // No gap on desktop
                        },
                            width: "100%"
                        }}
                    >
                        {binElements}
                    </MuiMaterials.Grid>
                </MuiMaterials.Box>
            )}
        </MuiMaterials.Box>
    );
}

export default SiteCard;